import React, { useCallback, useEffect, useMemo, useRef, useState, forwardRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import AccountDeletionPage from "@/AccountDeletionPage";
import SupportPage from "@/SupportPage";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Toaster, toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import {
  Download,
  FileText,
  Save,
  Plus,
  Trash2,
  Pencil,
  Phone,
  Mail,
  MapPin,
  User,
  Link as LinkIcon,
  RefreshCw,
  Copy,
  EyeOff,
  Upload,
  ShieldCheck,
  Lock,
  LogOut,
  KeyRound,
  PenTool,
  Send,
  Wallet,
  TrendingUp,
  AlertTriangle,
  Clock,
  X,
  ArrowRight,
  Building2,
  Users,
  Palette,
  Globe,
  Server,
  Rocket,
  Briefcase,
  QrCode,
  Star,
  Gift,
  ShoppingCart,
  Zap,
  Shield,
  CalendarDays,
  CheckCircle,
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  Euro,
  FlaskConical,
} from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas-pro";
import { PDFDocument } from "pdf-lib";
import { CalendarView } from "@/components/CalendarView";
import { CRMTab } from "@/components/CRMTab";
import { ReiseStudioTab } from "@/components/ReiseStudioTab";
import { LockedTab, ModuleUpsellDialog } from "@/components/ModuleUpsell";
import { Plane, Receipt } from "lucide-react";
import { LegalContent } from "@/components/LegalContent";
import LegalPage from "@/LegalPage";
import PublicOAuthLanding from "@/PublicOAuthLanding";
import GoogleApiPrivacyPage from "@/GoogleApiPrivacyPage";
import { SignaturePad } from "@/components/SignaturePad";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { WelcomePage } from "@/components/WelcomePage";
import { WhiteLabelWelcomePage } from "@/components/WhiteLabelWelcomePage";
import { ResetPasswordPage } from "@/components/ResetPasswordPage";
import InvitePage from "@/components/InvitePage";
import GoogleAuthCallback from "@/components/GoogleAuthCallback";
import UniversalAssistant from "@/components/UniversalAssistant";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { InquiriesTab } from "@/components/InquiriesTab";
import { MarketingTab } from "@/components/MarketingTab";
import { PaymentsTab } from "@/components/PaymentsTab";
import { SteuerTab } from "@/components/SteuerTab";
import { PlatformTab } from "@/components/PlatformTab";
import { AngeboteTab } from "@/components/AngeboteTab";
import { SettingsTab } from "@/components/SettingsTab";
import { KundenTab } from "@/components/KundenTab";
import { BetriebsausgabenTab } from "@/components/BetriebsausgabenTab";
import { WorkforceTab } from "@/components/WorkforceTab";
import { ExpenseDocumentTab } from "@/components/ExpenseDocumentTab";
import { CommerceTab } from "@/components/CommerceTab";
import { LeadFunnelTab } from "@/components/LeadFunnelTab";
import { PnpStudio } from "@/components/PnpStudio";
import { PublicCampaignLanding } from "@/components/PublicCampaignLanding";
import { GlobalSearch } from "@/components/GlobalSearch";
import { MeinTagView } from "@/components/MeinTagView";
import { BusinessCockpit } from "@/components/BusinessCockpit";
import { AutomationsView } from "@/components/AutomationsView";
import { Kunde360View, KundenSucheModal } from "@/components/Kunde360View";
import { ImportExportView } from "@/components/ImportExportView";
import { OnboardingAssistant, ApprovalView } from "@/components/OnboardingApprovalView";
import { Profile360Hub } from "@/components/Profile360Hub";
import { WhiteLabelCentral } from "@/components/WhiteLabelCentral";
import { CompanyLogo } from "@/components/CompanyLogo";
import { PublicQuote } from "@/components/PublicQuote";
import { LoginScreen } from "@/components/LoginScreen";
import { CustomerGate } from "@/components/CustomerGate";
import { PlanSelection } from "@/components/PlanSelection";
import { EditorTab } from "@/components/EditorTab";
import { DashboardTab } from "@/components/DashboardTab";
import { NotificationsBell } from "@/components/NotificationsBell";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { today, isDemoReadonly, plusDays, buildVertragPdfBytes, emptyQuote, COUNTRY_PRESETS } from "@/lib/editor-utils";
import { FIRMA, applyCompany } from "@/lib/branding";
import { fmtCurrency, fmtDate, quoteNet, trackingUrlFor } from "@/lib/quote-helpers";
import { CurrencyProvider } from "@/lib/currency-context";
import { APP_NAME, COLOR_FIELDS, INVOICE_TYPES, CUSTOMER_TOKEN_KEY, STATUS_META } from "@/lib/constants";
import { clearAdminToken, getAdminToken, saveAdminToken } from "@/lib/admin-session";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------- Auth ----------

// Attach the right token to every request
axios.interceptors.request.use((config) => {
  const url = config.url || "";
  const adminTok = getAdminToken();
  const custTok = sessionStorage.getItem(CUSTOMER_TOKEN_KEY);
  if (url.includes("/api/public/")) {
    if (custTok) config.headers.Authorization = `Bearer ${custTok}`;
  } else if (url.includes("/api/")) {
    // Prefer admin token; fall back to customer token (needed for public quote
    // page that reads /api/settings/steuerdaten + /api/settings/attachments)
    const t = adminTok || custTok;
    if (t) config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 403 && err?.response?.data?.detail === "DEMO_READONLY") {
      toast.info("🔒 Demo-Modus – Änderungen sind in der Vorführ-Version deaktiviert.");
      return Promise.reject(err);
    }
    if (err?.response?.status === 401) {
      const url = err.config?.url || "";
      if (url.includes("/api/public/")) {
        sessionStorage.removeItem(CUSTOMER_TOKEN_KEY);
      } else if (url.includes("/api/") && !url.includes("/api/auth/login")) {
        clearAdminToken();
      }
    }
    return Promise.reject(err);
  }
);



// ============================== Editor ==============================

const Editor = ({ adminUser, company, subscription, onCompanyChange, onLogout }) => {
  const planFeatures = (subscription && subscription.features) || [];
  const hasFeature = (f) => planFeatures.includes(f);
  const hasBilling = hasFeature("quotes");
  const isMitarbeiter = adminUser?.role === "mitarbeiter";
  // P & P ist ein bewusst nicht vermarktetes, internes Arbeitsstudio.
  // Die serverseitige Prüfung in /api/pnp ist zusätzlich maßgeblich.
  const isPnpUser = ["info@femmesmarketing.com", "echelon@magenta.de"].includes((adminUser?.email || "").toLowerCase());
  const [upsellModule, setUpsellModule] = useState(null);
  const openModulesUpgrade = () => {
    setUpsellModule(null);
    setTab("settings");
    setTimeout(() => {
      document.querySelector('[data-testid="settings-modules-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
  };
  const openBillingPortal = async () => {
    try {
      const r = await axios.post(`${API}/billing/portal`, { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch {
      toast.error("Kundenportal konnte nicht geöffnet werden");
    }
  };
  const cancelSubscription = async () => {
    if (!window.confirm("Abonnement zum Ende des bereits bezahlten Zeitraums kündigen? Es verlängert sich danach nicht mehr.")) return;
    try {
      const r = await axios.post(`${API}/billing/cancel`);
      toast.success(`Gekündigt. Zugang aktiv bis ${r.data.active_until ? new Date(r.data.active_until).toLocaleDateString("de-DE") : "Periodenende"}.`);
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kündigung fehlgeschlagen");
    }
  };
  const resumeSubscription = async () => {
    try {
      await axios.post(`${API}/billing/resume`);
      toast.success("Kündigung zurückgenommen – dein Abo verlängert sich wieder.");
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehlgeschlagen");
    }
  };
  const deleteSandbox = async () => {
    if (sandboxDeleteConfirmation.trim().toUpperCase() !== "LÖSCHEN") {
      toast.error("Bitte bestätige die Löschung mit LÖSCHEN.");
      return;
    }
    if (!sandboxDeletePassword) {
      toast.error("Bitte bestätige die Löschung mit deinem aktuellen Passwort.");
      return;
    }
    setSandboxDeleteBusy(true);
    try {
      const result = await axios.delete(`${API}/auth/account`, {
        data: { password: sandboxDeletePassword, confirmation: sandboxDeleteConfirmation },
      });
      toast.success(result.data?.message || "Sandbox und Testdaten wurden gelöscht.");
      setSandboxDeleteOpen(false);
      onLogout();
      window.setTimeout(() => { window.location.assign("/app"); }, 600);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Sandbox konnte nicht gelöscht werden");
    } finally {
      setSandboxDeleteBusy(false);
    }
  };
  const [quote, setQuote] = useState(() => emptyQuote("angebot", company));
  const [quotes, setQuotes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [tab, setTab] = useState("uebersicht");
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [signature, setSignature] = useState({ exists: false, data_url: "" });
  const [sigPadOpen, setSigPadOpen] = useState(false);
  const [sigDraft, setSigDraft] = useState("");
  const [autoSigOpen, setAutoSigOpen] = useState(false);
  const [autoSigName, setAutoSigName] = useState(company?.owner_name || company?.company_name || "");
  const [autoSigFont, setAutoSigFont] = useState("Sail");
  const [autoSigTagline, setAutoSigTagline] = useState(company?.company_name || "");
  // accent removed – tagline is all black
  const [autoSigUrls, setAutoSigUrls] = useState(company?.website || "");
  const [aiSigLoading, setAiSigLoading] = useState(false);
  const fileInputRefs = useRef({});
  const [steuer, setSteuer] = useState({
    steuernummer: "",
    ustid: "",
    finanzamt: "",
    tax_status: "regelbesteuert",
    iban: "",
    bic: "",
    bank: "",
    kontoinhaber: "",
  });
  const [notifications, setNotifications] = useState([]);
  const [packages, setPackages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwLoad, setPwLoad] = useState(false);
  const [sandboxDeleteOpen, setSandboxDeleteOpen] = useState(false);
  const [sandboxDeletePassword, setSandboxDeletePassword] = useState("");
  const [sandboxDeleteConfirmation, setSandboxDeleteConfirmation] = useState("");
  const [sandboxDeleteBusy, setSandboxDeleteBusy] = useState(false);
  const previewRef = useRef(null);

  const total = useMemo(() => quoteNet(quote), [quote]);

  const update = (k, v) => setQuote((q) => ({ ...q, [k]: v }));

  // Kleinunternehmerregelung (§ 19 UStG): keine USt auf Dokumenten
  useEffect(() => {
    if (steuer?.tax_status === "kleinunternehmer" && quote.tax_rate !== 0) {
      setQuote((q) => ({ ...q, tax_rate: 0 }));
    }
  }, [steuer?.tax_status, quote.tax_rate]);

  // Mobile preview auto-scaling: fit fixed 794px A4 preview into available width
  useEffect(() => {
    const computePreviewScale = () => {
      const wrappers = document.querySelectorAll(
        '[data-testid="preview-wrapper"]'
      );
      wrappers.forEach((w) => {
        const innerWidth = w.clientWidth - 4; // small inset
        // On desktop (>=768) we already have enough space; keep 1.
        const isMobile = window.matchMedia("(max-width: 767px)").matches;
        const scale = isMobile
          ? Math.min(1, Math.max(0.3, innerWidth / 794))
          : 1;
        w.style.setProperty("--preview-scale", String(scale));
        const previewEl = w.querySelector("#quote-preview");
        if (previewEl) {
          const h = previewEl.scrollHeight || 1123;
          w.style.setProperty("--preview-height-est", `${h}px`);
        }
      });
    };
    computePreviewScale();
    window.addEventListener("resize", computePreviewScale);
    // also recompute after fonts load / images settle
    const t1 = setTimeout(computePreviewScale, 300);
    const t2 = setTimeout(computePreviewScale, 1200);
    return () => {
      window.removeEventListener("resize", computePreviewScale);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [tab, quote]);

  const addPaket = useCallback((paket) => {
    setQuote((q) => ({
      ...q,
      positions: [
        ...(q.positions || []),
        {
          id: `${paket.id}-${(q.positions || []).length}-${Math.random().toString(36).slice(2, 8)}`,
          titel: paket.titel,
          bullets: [...paket.bullets],
          preis: paket.preis,
          quantity: 1,
          einheit: paket.einheit || "",
          // Das erste hinzugefügte Paket ist die konkrete Angebotsposition.
          // Weitere Pakete bleiben bewusst optional, damit Alternativen nicht addiert werden.
          include_in_total: !(q.positions || []).some((position) => position.include_in_total),
        },
      ],
    }));
  }, []);

  const addCustomPosition = useCallback(() => {
    setQuote((q) => ({
      ...q,
      positions: [
        ...(q.positions || []),
        {
          id: `custom-${(q.positions || []).length}-${Math.random().toString(36).slice(2, 8)}`,
          titel: "Eigene Leistung",
          bullets: [""],
          preis: 0,
          quantity: 1,
          einheit: "",
          include_in_total: true,
        },
      ],
    }));
  }, []);

  const updatePosition = (idx, patch) => {
    setQuote((q) => {
      const arr = [...(q.positions || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...q, positions: arr };
    });
  };

  const removePosition = (idx) => {
    setQuote((q) => {
      const arr = [...(q.positions || [])];
      arr.splice(idx, 1);
      return { ...q, positions: arr };
    });
  };

  const loadQuotes = async () => {
    try {
      const res = await axios.get(`${API}/quotes`);
      setQuotes(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchNextNumber = async (typ) => {
    try {
      const t = typ || quote.dokument_typ || "angebot";
      const res = await axios.get(`${API}/quotes/next-number`, {
        params: { typ: t },
      });
      setQuote((q) => ({ ...q, angebot_nr: res.data.next }));
    } catch (e) {
      console.error(e);
    }
  };

  const fetchAttachments = async () => {
    try {
      const res = await axios.get(`${API}/settings/attachments`);
      setAttachments(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const uploadAttachment = async (file, label) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Bitte eine PDF-Datei wählen");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 8 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = (reader.result?.toString() || "").split(",")[1] || "";
      try {
        await axios.post(`${API}/settings/attachments`, {
          label: label || file.name.replace(/\.pdf$/i, ""),
          filename: file.name,
          data_base64: b64,
        });
        toast.success(`${file.name} hochgeladen`);
        fetchAttachments();
      } catch {
        toast.error("Upload fehlgeschlagen");
      }
    };
    reader.readAsDataURL(file);
  };

  const renameAttachment = async (id, label) => {
    try {
      await axios.put(`${API}/settings/attachments/${id}`, { label });
      fetchAttachments();
    } catch {
      toast.error("Umbenennen fehlgeschlagen");
    }
  };

  const removeAttachment = async (id) => {
    if (!window.confirm("Dokument wirklich entfernen?")) return;
    try {
      await axios.delete(`${API}/settings/attachments/${id}`);
      toast.success("Entfernt");
      fetchAttachments();
    } catch {
      toast.error("Entfernen fehlgeschlagen");
    }
  };

  const fetchSignature = async () => {
    try {
      const res = await axios.get(`${API}/settings/signature`);
      setSignature({
        exists: !!res.data.exists,
        data_url: res.data.data_url || "",
      });
    } catch (e) {
      console.error(e);
    }
  };

  const saveDrawnSignature = async () => {
    if (!sigDraft) {
      toast.error("Bitte zuerst unterschreiben");
      return;
    }
    try {
      await axios.post(`${API}/settings/signature`, { data_url: sigDraft });
      toast.success("Unterschrift gespeichert");
      setSigPadOpen(false);
      setSigDraft("");
      fetchSignature();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const uploadSignatureFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte ein Bild (PNG/JPG) wählen");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result?.toString() || "";
      try {
        await axios.post(`${API}/settings/signature`, { data_url: dataUrl });
        toast.success("Unterschrift hochgeladen");
        fetchSignature();
      } catch {
        toast.error("Upload fehlgeschlagen");
      }
    };
    reader.readAsDataURL(file);
  };

  const removeSignature = async () => {
    if (!window.confirm("Unterschrift wirklich entfernen?")) return;
    try {
      await axios.delete(`${API}/settings/signature`);
      toast.success("Entfernt");
      fetchSignature();
    } catch {
      toast.error("Entfernen fehlgeschlagen");
    }
  };

  const renderTextSignature = (name, fontName) => {
    const canvas = document.createElement("canvas");
    canvas.width = 900;
    canvas.height = 360;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";

    // Big handwritten signature line
    const SIG_SIZES = {
      Sail: 120,
      Cookie: 110,
      Whisper: 140,
      Birthstone: 130,
      "Mea Culpa": 120,
      "Imperial Script": 130,
      "Bonheur Royale": 120,
      "Style Script": 120,
      "Send Flowers": 120,
      Italianno: 160,
      "Petit Formal Script": 110,
      "Mrs Saint Delafield": 130,
      Tangerine: 170,
      "Dr Sugiyama": 140,
      Parisienne: 120,
      "Homemade Apple": 90,
      "La Belle Aurore": 100,
      Sacramento: 120,
      Allura: 140,
      "Pinyon Script": 130,
      Yellowtail: 110,
    };
    const sigSize = SIG_SIZES[fontName] || 120;
    ctx.fillStyle = "var(--brand-ink)";
    ctx.font = `${sigSize}px "${fontName}", cursive`;
    ctx.fillText(name, 40, 170);

    // Tagline (sans-serif) – all black, no accent color
    if (autoSigTagline.trim()) {
      ctx.font = `500 30px "Inter", sans-serif`;
      ctx.fillStyle = "var(--brand-ink)";
      ctx.fillText(autoSigTagline, 40, 245);
    }

    // URLs (lighter, smaller)
    ctx.fillStyle = "#5b5b5b";
    ctx.font = `400 22px "Inter", sans-serif`;
    const urlLines = autoSigUrls.split("\n").map((u) => u.trim()).filter(Boolean);
    urlLines.forEach((u, i) => {
      ctx.fillText(u, 40, 290 + i * 30);
    });

    return canvas.toDataURL("image/png");
  };

  const saveAutoSignature = async () => {
    if (!autoSigName.trim()) {
      toast.error("Bitte Namen eingeben");
      return;
    }
    try {
      if (document.fonts && document.fonts.load) {
        await Promise.all([
          document.fonts.load(`140px "${autoSigFont}"`),
          document.fonts.load(`500 28px "Inter"`),
          document.fonts.load(`400 22px "Inter"`),
        ]);
      }
      const dataUrl = renderTextSignature(autoSigName, autoSigFont);
      await axios.post(`${API}/settings/signature`, { data_url: dataUrl });
      toast.success("Signatur gespeichert");
      setAutoSigOpen(false);
      fetchSignature();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const generateAiSignature = async () => {
    if (!autoSigName.trim()) {
      toast.error("Bitte Namen eingeben");
      return;
    }
    setAiSigLoading(true);
    toast.loading("KI generiert deine Unterschrift …", { id: "ai-sig" });
    try {
      await axios.post(`${API}/settings/signature/generate`, {
        name: autoSigName,
        style_hint: "elegant flowing cursive",
      });
      toast.success("KI-Unterschrift erstellt", { id: "ai-sig" });
      setAutoSigOpen(false);
      fetchSignature();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Generierung fehlgeschlagen", {
        id: "ai-sig",
      });
    } finally {
      setAiSigLoading(false);
    }
  };

  const toggleAttachment = (slot) => {
    setQuote((q) => {
      const cur = Array.isArray(q.attachments) ? q.attachments : [];
      const next = cur.includes(slot)
        ? cur.filter((s) => s !== slot)
        : [...cur, slot];
      return { ...q, attachments: next };
    });
  };

  const fetchSteuer = async () => {
    try {
      const res = await axios.get(`${API}/settings/steuerdaten`);
      setSteuer({
        steuernummer: res.data.steuernummer || "",
        ustid: res.data.ustid || "",
        finanzamt: res.data.finanzamt || "",
        iban: res.data.iban || "",
        bic: res.data.bic || "",
        bank: res.data.bank || "",
        kontoinhaber: res.data.kontoinhaber || "",
        tax_status: res.data.tax_status || "regelbesteuert",
      });
    } catch (e) {
      console.error(e);
    }
  };

  const saveSteuer = async () => {
    try {
      await axios.post(`${API}/settings/steuerdaten`, steuer);
      toast.success("Steuerdaten gespeichert");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const toggleKleinunternehmer = async (on) => {
    const next = { ...steuer, tax_status: on ? "kleinunternehmer" : "regelbesteuert" };
    setSteuer(next);
    if (on) update("tax_rate", 0);
    try {
      await axios.post(`${API}/settings/steuerdaten`, next);
      toast.success(on
        ? "Kleinunternehmerregelung (§ 19 UStG) aktiviert – keine USt auf Dokumenten"
        : "Regelbesteuerung aktiviert");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const [datev, setDatev] = useState({
    berater_nr: "", mandant_nr: "", konto_laenge: 4,
    debitor_sammelkonto: "10000", erloes_19: "8400", erloes_7: "8300", erloes_0: "8200",
  });
  const fetchDatev = async () => {
    try {
      const res = await axios.get(`${API}/settings/datev`);
      setDatev((d) => ({ ...d, ...(res.data || {}) }));
    } catch (e) { console.error(e); }
  };
  const saveDatev = async () => {
    try {
      await axios.post(`${API}/settings/datev`, datev);
      toast.success("DATEV-Einstellungen gespeichert");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const fetchNotifications = async () => {
    try {
      const [list, count] = await Promise.all([
        axios.get(`${API}/notifications`),
        axios.get(`${API}/notifications/unread-count`),
      ]);
      setNotifications(list.data);
      setUnread(count.data.unread || 0);
    } catch (e) {
      console.error(e);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      fetchNotifications();
    } catch (e) {
      console.error(e);
    }
  };

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${API}/packages`);
      setPackages(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/customers`);
      setCustomers(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const applyCustomer = (c) => {
    setQuote((q) => ({
      ...q,
      anrede: c.anrede || q.anrede,
      kunde_name: c.kunde_name || "",
      kunde_firma: c.kunde_firma || "",
      kunde_strasse: c.kunde_strasse || "",
      kunde_plz_ort: c.kunde_plz_ort || "",
      kunde_land: c.kunde_land || q.kunde_land,
      kunde_email: c.kunde_email || "",
      kunde_telefon: c.kunde_telefon || "",
      kunde_ustid: c.kunde_ustid || "",
      kunde_einvoice_endpoint: c.kunde_einvoice_endpoint || "",
      kunde_einvoice_endpoint_scheme: c.kunde_einvoice_endpoint_scheme || "",
      kunde_buyer_reference: c.kunde_buyer_reference || "",
    }));
    toast.success(`Kunde „${c.kunde_name}“ übernommen`);
  };

  const saveAsTemplate = async () => {
    const typ = quote.dokument_typ || "angebot";
    const data = {
      leistung_titel: quote.leistung_titel,
      leistung_beschreibung: quote.leistung_beschreibung,
      einleitung: quote.einleitung,
      leistungsdatum: quote.leistungsdatum,
      zahlungsziel: quote.zahlungsziel,
      notizen: quote.notizen,
      attachments: quote.attachments,
      positions: quote.positions,
      include_vertrag: quote.include_vertrag,
    };
    try {
      await axios.post(`${API}/templates/${typ}`, data);
      toast.success("Als Standardvorlage gespeichert");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const applyTemplate = async (typ) => {
    try {
      const res = await axios.get(`${API}/templates/${typ}`);
      const t = res.data || {};
      if (t && Object.keys(t).length > 0) {
        setQuote((q) => ({ ...q, ...t }));
      }
      return t;
    } catch {
      return {};
    }
  };

  // ----- Company / Branding settings -----
  const [companyForm, setCompanyForm] = useState(() => ({ ...(company || {}) }));
  const setC = (k, v) => setCompanyForm((f) => ({ ...f, [k]: v }));

  const onCompanyLogo = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte eine Bilddatei wählen");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo zu groß (max. 2 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setC("logo_base64", reader.result?.toString() || "");
    reader.readAsDataURL(file);
  };

  const saveCompany = async () => {
    try {
      await axios.post(`${API}/settings/company`, {
        company_name: companyForm.company_name || "",
        owner_name: companyForm.owner_name || "",
        logo_base64: companyForm.logo_base64 || "",
        strasse: companyForm.strasse || "",
        plz_ort: companyForm.plz_ort || "",
        land: companyForm.land || "Deutschland",
        main_country: companyForm.main_country || "",
        base_currency: companyForm.base_currency || "",
        locale: companyForm.locale || "",
        time_zone: companyForm.time_zone || "",
        languages: companyForm.languages || [],
        offer_label: companyForm.offer_label || "",
        telefon: companyForm.telefon || "",
        email: companyForm.email || "",
        website: companyForm.website || "",
        ustid: companyForm.ustid || "",
        color_primary: companyForm.color_primary || "#2C3338",
        color_primary_dark: companyForm.color_primary_dark || "#1B1F23",
        color_bg: companyForm.color_bg || "#F4F5F6",
        color_border: companyForm.color_border || "#DADEE2",
        color_ink: companyForm.color_ink || "#1A1D21",
        disclaimer: companyForm.disclaimer || "",
        customer_password: companyForm.customer_password || "",
        link_password_protect: companyForm.link_password_protect !== false,
        demo_cta_label: companyForm.demo_cta_label || "",
        demo_cta_url: companyForm.demo_cta_url || "",
      });
      applyCompany(companyForm);
      toast.success("Firmendaten gespeichert");
      if (onCompanyChange) await onCompanyChange();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const setCompanyColor = (k, cssVar, v) => {
    setC(k, v);
    if (typeof document !== "undefined") {
      document.documentElement.style.setProperty(cssVar, v);
    }
  };

  // ----- Package library management -----
  const [newPkg, setNewPkg] = useState({ titel: "", preis: 0, einheit: "", bullets: "" });

  const createPackage = async () => {
    if (!newPkg.titel.trim()) {
      toast.error("Bitte einen Pakettitel angeben");
      return;
    }
    try {
      await axios.post(`${API}/packages`, {
        titel: newPkg.titel,
        preis: Number(newPkg.preis) || 0,
        einheit: newPkg.einheit,
        bullets: newPkg.bullets.split("\n").map((b) => b.trim()).filter(Boolean),
      });
      setNewPkg({ titel: "", preis: 0, einheit: "", bullets: "" });
      toast.success("Paket erstellt");
      fetchPackages();
    } catch {
      toast.error("Erstellen fehlgeschlagen");
    }
  };

  const deletePackage = async (id) => {
    if (!window.confirm("Paket wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/packages/${id}`);
      fetchPackages();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const resetStandardPackages = async () => {
    try {
      await axios.post(`${API}/packages/reset-standard`);
      toast.success("Standard-Pakete hinzugefügt");
      fetchPackages();
    } catch {
      toast.error("Fehlgeschlagen");
    }
  };

  // ----- Payment settings -----
  const [payCfg, setPayCfg] = useState({ stripe_enabled: false, has_stripe_key: false, paypal_enabled: false, has_paypal_key: false, paypal_mode: "sandbox" });
  const [payForm, setPayForm] = useState({ stripe_secret_key: "", paypal_client_id: "", paypal_secret: "", paypal_mode: "sandbox", stripe_enabled: false, paypal_enabled: false });

  const fetchPayment = async () => {
    try {
      const res = await axios.get(`${API}/settings/payment`);
      setPayCfg(res.data || {});
      setPayForm((f) => ({
        ...f,
        stripe_enabled: !!res.data?.stripe_enabled,
        paypal_enabled: !!res.data?.paypal_enabled,
        paypal_mode: res.data?.paypal_mode || "sandbox",
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const savePayment = async () => {
    try {
      await axios.post(`${API}/settings/payment`, payForm);
      toast.success("Zahlungseinstellungen gespeichert");
      setPayForm((f) => ({ ...f, stripe_secret_key: "", paypal_secret: "" }));
      fetchPayment();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  // ----- E-Mail settings -----
  const [emailCfg, setEmailCfg] = useState({ enabled: false, has_api_key: false, sender_email: "", sender_name: "" });
  const [emailForm, setEmailForm] = useState({ resend_api_key: "", sender_email: "", sender_name: "", enabled: false });

  const fetchEmail = async () => {
    try {
      const res = await axios.get(`${API}/settings/email`);
      setEmailCfg(res.data || {});
      setEmailForm((f) => ({
        ...f,
        sender_email: res.data?.sender_email || "",
        sender_name: res.data?.sender_name || "",
        enabled: !!res.data?.enabled,
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const saveEmail = async () => {
    try {
      await axios.post(`${API}/settings/email`, emailForm);
      toast.success("E-Mail-Einstellungen gespeichert");
      setEmailForm((f) => ({ ...f, resend_api_key: "" }));
      fetchEmail();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  // ----- Zahlungserinnerungen / Mahnlauf -----
  const [remindersForm, setRemindersForm] = useState({
    enabled: false, days: [7, 14, 30], labels: ["Zahlungserinnerung", "1. Mahnung", "2. Mahnung"],
    predue_enabled: false, predue_days: 3,
    followup_enabled: false, followup_days: 7,
  });
  const [remindersSaving, setRemindersSaving] = useState(false);

  const fetchReminders = async () => {
    try {
      const res = await axios.get(`${API}/settings/reminders`);
      if (res.data) setRemindersForm((f) => ({ ...f, ...res.data }));
    } catch (e) {
      console.error(e);
    }
  };

  const saveReminders = async () => {
    setRemindersSaving(true);
    try {
      await axios.post(`${API}/settings/reminders`, remindersForm);
      toast.success("Erinnerungs-Einstellungen gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setRemindersSaving(false);
    }
  };

  const runPredueNow = async () => {
    try {
      const res = await axios.post(`${API}/quotes/run-predue`, { origin_url: window.location.origin });
      toast.success(`Vorab-Erinnerungen versendet: ${res.data?.sent ?? 0}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    }
  };

  const runFollowupNow = async () => {
    try {
      const res = await axios.post(`${API}/quotes/run-followup`, { origin_url: window.location.origin });
      toast.success(`Angebots-Follow-ups versendet: ${res.data?.sent ?? 0}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    }
  };

  const sendByEmail = async () => {
    if (!activeId) {
      toast.error("Bitte zuerst speichern");
      return;
    }
    if (!quote.kunde_email) {
      toast.error("Kunde hat keine E-Mail-Adresse");
      return;
    }
    try {
      let pdf_base64 = "";
      if (INVOICE_TYPES.includes(quote.dokument_typ) && ["DE", "AT"].includes((quote.country || "DE").toUpperCase())) {
        try {
          const bytes = await buildPdfBytes();
          pdf_base64 = bytesToBase64(bytes);
        } catch (_) { /* fallback: backend attaches XML */ }
      }
      const res = await axios.post(`${API}/quotes/${activeId}/send-email`, {
        origin_url: window.location.origin,
        pdf_base64,
      });
      const sentAt = res.data?.sent_at;
      setQuote((q) => ({ ...q, status: "sent", ...(sentAt ? { sent_at: sentAt } : {}) }));
      setQuotes((qs) => qs.map((x) => (x.id === activeId ? { ...x, status: "sent", ...(sentAt ? { sent_at: sentAt } : {}) } : x)));
      toast.success(`E-Mail an ${quote.kunde_email} gesendet`);
      return res;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "E-Mail konnte nicht gesendet werden");
    }
  };

  useEffect(() => {
    void Promise.resolve().then(() => {
      loadQuotes();
      fetchNextNumber();
      fetchAttachments();
      fetchSteuer();
      fetchSignature();
      fetchNotifications();
      fetchPackages();
      fetchCustomers();
      fetchNamedTemplates();
      fetchDatev();
      fetchPayment();
      fetchEmail();
      fetchReminders();
    });
    const t = setInterval(() => fetchNotifications(), 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newQuote = async (typ = "angebot") => {
    setActiveId(null);
    setQuote(emptyQuote(typ, company));
    await fetchNextNumber(typ);
    await applyTemplate(typ);
    setTab("editor");
    toast.success(
      typ === "rechnung" ? "Neue Rechnung gestartet" : "Neues Angebot gestartet"
    );
  };

  const newDocumentForCustomer = async (c, typ) => {
    await newQuote(typ);
    setQuote((q) => ({
      ...q,
      anrede: c.anrede || q.anrede,
      kunde_name: c.kunde_name || "",
      kunde_firma: c.kunde_firma || "",
      kunde_email: c.kunde_email || "",
      kunde_telefon: c.kunde_telefon || "",
      kunde_strasse: c.kunde_strasse || "",
      kunde_plz_ort: c.kunde_plz_ort || "",
      kunde_land: c.kunde_land || q.kunde_land,
      kunde_ustid: c.kunde_ustid || "",
      kunde_einvoice_endpoint: c.kunde_einvoice_endpoint || "",
      kunde_einvoice_endpoint_scheme: c.kunde_einvoice_endpoint_scheme || "",
      kunde_buyer_reference: c.kunde_buyer_reference || "",
    }));
    const label = typ === "rechnung" ? "Neue Rechnung" : "Neues Angebot";
    toast.success(`${label} für ${c.kunde_firma || c.kunde_name}`);
  };
  const newQuoteForCustomer = (c) => newDocumentForCustomer(c, "angebot");
  const newInvoiceForCustomer = (c) => newDocumentForCustomer(c, "rechnung");

  const createQuoteFromFunnelLead = async (lead) => {
    const fields = lead?.fields || {};
    await newQuote("angebot");
    const service = fields.leistung || fields.interesse || lead?.campaign_name || "";
    const description = fields.beschreibung || "";
    setQuote((q) => ({
      ...q,
      kunde_name: fields.name || "",
      kunde_firma: fields.unternehmen || "",
      kunde_email: fields.email || "",
      kunde_telefon: fields.telefon || "",
      kunde_plz_ort: fields.plz_ort || "",
      leistung_titel: service,
      leistung_beschreibung: description,
      positions: service
        ? [{ id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()), titel: service, bullets: description ? [description] : [], preis: 0, quantity: 1, einheit: "", include_in_total: true }]
        : (q.positions || []),
    }));
    setTab("editor");
    toast.success(`Angebot aus Lead „${fields.unternehmen || fields.name || "Interessent"}“ vorbereitet`);
  };

  const createQuoteFromDeal = async (deal) => {
    await newQuote("angebot");
    const val = Number(deal.value) || 0;
    setQuote((q) => ({
      ...q,
      kunde_firma: deal.customer_name || "",
      leistung_titel: deal.title || "",
      deal_id: deal.id || "",
      positions: val > 0
        ? [{ id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()), titel: deal.title || "Leistung", bullets: [], preis: val, quantity: 1, einheit: "", include_in_total: true }]
        : (q.positions || []),
    }));
    setTab("editor");
    toast.success(`Angebot aus Deal „${deal.title}" gestartet`);
  };

  const switchDokumentTyp = async (typ) => {
    if (activeId && typ !== quote.dokument_typ) {
      toast.error("Ein gespeichertes Dokument wird nicht direkt umbenannt. Nutze bitte „Dokument umwandeln“, damit Angebot und Rechnung verbunden bleiben.");
      return;
    }
    if (!activeId) {
      setQuote((q) => ({ ...q, dokument_typ: typ }));
      await fetchNextNumber(typ);
    }
  };

  const convertToInvoice = async () => {
    if (!activeId) {
      toast.error("Bitte zuerst speichern.");
      return;
    }
    try {
      const res = await axios.post(`${API}/quotes/${activeId}/to-invoice`);
      setActiveId(res.data.id);
      setQuote(res.data);
      loadQuotes();
      toast.success(`Rechnung ${res.data.angebot_nr} erstellt`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Rechnung nicht erstellen");
    }
  };

  const convertOneToInvoice = async (id) => {
    try {
      const res = await axios.post(`${API}/quotes/${id}/to-invoice`);
      await loadQuotes();
      setActiveId(res.data.id);
      setQuote(res.data);
      setTab("editor");
      toast.success(`Rechnung ${res.data.angebot_nr} erstellt`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Rechnung nicht erstellen");
    }
  };

  const convertTo = async (endpoint, label) => {
    if (!activeId) {
      toast.error("Bitte zuerst speichern.");
      return;
    }
    try {
      const res = await axios.post(`${API}/quotes/${activeId}/${endpoint}`);
      setActiveId(res.data.id);
      setQuote(res.data);
      loadQuotes();
      toast.success(`${label} ${res.data.angebot_nr} erstellt`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : `Konnte ${label} nicht erstellen`);
    }
  };

  const duplicateOne = async (id) => {
    try {
      const res = await axios.post(`${API}/quotes/${id}/duplicate`);
      setActiveId(res.data.id);
      setQuote(res.data);
      await loadQuotes();
      setTab("editor");
      toast.success(`Dupliziert: ${res.data.angebot_nr}`);
    } catch (e) {
      toast.error("Duplizieren fehlgeschlagen");
    }
  };

  const save = async () => {
    try {
      if (activeId) {
        const res = await axios.put(`${API}/quotes/${activeId}`, quote);
        setQuote(res.data);
        toast.success("Angebot aktualisiert");
      } else {
        const res = await axios.post(`${API}/quotes`, quote);
        setActiveId(res.data.id);
        setQuote(res.data);
        toast.success("Angebot gespeichert");
      }
      loadQuotes();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Speichern fehlgeschlagen");
    }
  };

  const loadOne = async (id) => {
    try {
      const res = await axios.get(`${API}/quotes/${id}`);
      setActiveId(id);
      setQuote(res.data);
      setTab("editor");
    } catch {
      toast.error("Konnte Angebot nicht laden");
    }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Dieses Angebot wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`);
      toast.success("Gelöscht");
      if (activeId === id) {
        setActiveId(null);
        setQuote(emptyQuote("angebot", company));
        await fetchNextNumber();
      }
      loadQuotes();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const changeStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/quotes/${id}/status`, {
        status,
        origin_url: window.location.origin,
      });
      const sentAt = res.data?.sent_at;
      setQuotes((qs) => qs.map((x) => (x.id === id ? { ...x, status, ...(sentAt ? { sent_at: sentAt } : {}) } : x)));
      if (activeId === id) setQuote((q) => ({ ...q, status, ...(sentAt ? { sent_at: sentAt } : {}) }));
      if (status === "sent") {
        toast.success("Status: Versendet. Eine E-Mail wird ausschließlich über den E-Mail-Button versendet.");
      } else if (status === "accepted") {
        const q = quotes.find((x) => x.id === id);
        const isAngebot = !q || q.dokument_typ === "angebot";
        if (isAngebot) {
          toast.success("Angenommen 🎉 · verknüpfter Deal auf Gewonnen gesetzt", {
            description: "Jetzt direkt in eine Rechnung umwandeln?",
            action: { label: "→ Rechnung", onClick: () => convertOneToInvoice(id) },
          });
        } else {
          toast.success("Status: Angenommen");
        }
      } else {
        toast.success(`Status: ${STATUS_META[status]?.label || status}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 401) {
        toast.error("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");
        window.setTimeout(() => window.location.reload(), 700);
      } else {
        toast.error(typeof detail === "string" ? detail : "Status konnte nicht geändert werden");
      }
    }
  };

  const copyTracking = async () => {
    const url = trackingUrlFor(quote.public_token);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Tracking-Link kopiert");
    } catch {
      toast.error("Konnte nicht kopieren");
    }
  };

  const changePassword = async () => {
    if (pwNew.length < 8) {
      toast.error("Neues Passwort muss mindestens 8 Zeichen haben");
      return;
    }
    setPwLoad(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: pwOld,
        new_password: pwNew,
      });
      toast.success("Passwort geändert");
      setPwOpen(false);
      setPwOld("");
      setPwNew("");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Änderung fehlgeschlagen");
    } finally {
      setPwLoad(false);
    }
  };

  const buildPdfBytes = async () => {
    const node = previewRef.current;
    if (!node) throw new Error("Vorschau nicht bereit");
    {
      // 1) Render the full preview as one big canvas
      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const SCALE = 2;
      const previewRect = node.getBoundingClientRect();

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pageW = pdf.internal.pageSize.getWidth(); // 210
      const pageH = pdf.internal.pageSize.getHeight(); // 297
      const margin = 12; // Großzügiger Druckrand
      const contentW = pageW - margin * 2; // 186 mm
      const usableH = pageH - margin * 2; // 273 mm
      // Conversion: master canvas width → contentW mm (preserves aspect ratio)
      const pxToMm = contentW / canvas.width;
      // Vertical breathing room between sections (so PDF doesn't look cramped)
      const SECTION_GAP = 4; // mm

      // 2) Collect all marked sections
      const sectionEls = Array.from(
        node.querySelectorAll("[data-pdf-section]")
      );

      // Helper: crop a region from the master canvas into a sub-canvas
      const cropToDataUrl = (left, top, w, h) => {
        const sub = document.createElement("canvas");
        sub.width = w;
        sub.height = h;
        const ctx = sub.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(canvas, left, top, w, h, 0, 0, w, h);
        return sub.toDataURL("image/png");
      };

      let cursorY = margin;
      let firstOnPage = true;
      for (const sec of sectionEls) {
        const r = sec.getBoundingClientRect();
        // Capture a full-width horizontal strip from the master canvas so the
        // section keeps its natural layout (padding, alignment) and proportions.
        const top = Math.max(0, (r.top - previewRect.top) * SCALE);
        const h = Math.min(canvas.height - top, r.height * SCALE);
        if (h <= 0) continue;

        const secH_mm = h * pxToMm;

        // If section won't fit (even with required gap), start a new page (unless we're already at top)
        if (!firstOnPage && cursorY + secH_mm > pageH - margin) {
          pdf.addPage();
          cursorY = margin;
          firstOnPage = true;
        }

        // Section taller than full A4 page → split across pages
        if (secH_mm > usableH) {
          let remainingPx = h;
          let offsetPx = 0;
          while (remainingPx > 0) {
            const slicePx = Math.min(
              remainingPx,
              Math.floor(usableH / pxToMm)
            );
            const dataUrl = cropToDataUrl(0, top + offsetPx, canvas.width, slicePx);
            const sliceH_mm = slicePx * pxToMm;
            if (!firstOnPage) {
              pdf.addPage();
              cursorY = margin;
            }
            pdf.addImage(dataUrl, "PNG", margin, cursorY, contentW, sliceH_mm);
            cursorY += sliceH_mm + SECTION_GAP;
            firstOnPage = false;
            remainingPx -= slicePx;
            offsetPx += slicePx;
          }
        } else {
          const dataUrl = cropToDataUrl(0, top, canvas.width, h);
          pdf.addImage(dataUrl, "PNG", margin, cursorY, contentW, secH_mm);
          cursorY += secH_mm + SECTION_GAP;
          firstOnPage = false;
        }
      }

      // Get base PDF bytes
      let finalBytes = pdf.output("arraybuffer");

      // Merge selected attachments + optional generated Dienstleistungsvertrag
      const selectedIds = (quote.attachments || []).filter((id) =>
        attachments.find((a) => a.id === id)
      );
      const wantsVertrag = !!quote.include_vertrag;
      if (selectedIds.length > 0 || wantsVertrag) {
        try {
          const merged = await PDFDocument.create();
          const basePdf = await PDFDocument.load(finalBytes);
          const baseCopied = await merged.copyPages(
            basePdf,
            basePdf.getPageIndices()
          );
          baseCopied.forEach((p) => merged.addPage(p));

          // Keep the order in which attachments appear in the library
          const ordered = attachments
            .map((a) => a.id)
            .filter((id) => selectedIds.includes(id));
          for (const attId of ordered) {
            try {
              const res = await axios.get(
                `${API}/settings/attachments/${attId}`
              );
              if (res.data.exists && res.data.data_base64) {
                const bytes = Uint8Array.from(
                  atob(res.data.data_base64),
                  (c) => c.charCodeAt(0)
                );
                const att = await PDFDocument.load(bytes);
                const copied = await merged.copyPages(
                  att,
                  att.getPageIndices()
                );
                copied.forEach((p) => merged.addPage(p));
              }
            } catch (e) {
              console.error(`attachment ${attId} merge failed`, e);
            }
          }

          // Generated Dienstleistungsvertrag (always last)
          if (wantsVertrag) {
            try {
              const vertragBytes = buildVertragPdfBytes(quote, total);
              const vertragPdf = await PDFDocument.load(vertragBytes);
              const copied = await merged.copyPages(
                vertragPdf,
                vertragPdf.getPageIndices()
              );
              copied.forEach((p) => merged.addPage(p));
            } catch (e) {
              console.error("Vertrag generation failed", e);
            }
          }

          finalBytes = await merged.save();
        } catch (err) {
          console.error("Attachment merge failed", err);
          toast.warning("Anlagen konnten nicht angehängt werden.");
        }
      }

      return finalBytes;
    }
  };

  const pdfFilename = (ext = "pdf") => {
    const docKind = quote.dokument_typ === "rechnung" ? "Rechnung" : "Angebot";
    const cleanName = (s) =>
      (s || "")
        .toString()
        .trim()
        .replace(/[\\/:*?"<>|]/g, "")
        .replace(/\s+/g, "_");
    const kundeForFile =
      cleanName(quote.kunde_firma) ||
      cleanName(quote.kunde_name) ||
      "ohne-Kundenname";
    return `${docKind}_${quote.angebot_nr || "neu"}_${kundeForFile}.${ext}`;
  };

  const triggerDownload = (bytes, filename, mime) => {
    const blob = new Blob([bytes], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const bytesToBase64 = (bytes) => {
    let binary = "";
    const arr = new Uint8Array(bytes);
    const chunk = 0x8000;
    for (let i = 0; i < arr.length; i += chunk) {
      binary += String.fromCharCode.apply(null, arr.subarray(i, i + chunk));
    }
    return btoa(binary);
  };

  const downloadPdf = async () => {
    toast.loading("PDF wird erstellt …", { id: "pdf" });
    try {
      const finalBytes = await buildPdfBytes();
      triggerDownload(finalBytes, pdfFilename("pdf"), "application/pdf");
      toast.success("PDF heruntergeladen", { id: "pdf" });
    } catch (e) {
      console.error(e);
      toast.error("PDF konnte nicht erstellt werden", { id: "pdf" });
    }
  };

  // ---- E-Rechnung (EN 16931) Export ----
  const [eInvoiceOpen, setEInvoiceOpen] = useState(false);
  const [eInvoiceMissing, setEInvoiceMissing] = useState([]);

  const checkEInvoice = async () => {
    if (!activeId) {
      toast.error("Bitte zuerst speichern");
      return false;
    }
    try {
      const res = await axios.get(`${API}/quotes/${activeId}/einvoice/validate`);
      if (res.data.valid) {
        setEInvoiceMissing([]);
        return true;
      }
      setEInvoiceMissing(res.data.missing || []);
      return false;
    } catch {
      toast.error("Prüfung fehlgeschlagen");
      return false;
    }
  };

  const exportEInvoiceXml = async (flavour) => {
    const ok = await checkEInvoice();
    if (!ok) return;
    toast.loading("E-Rechnung wird erzeugt …", { id: "einv" });
    try {
      const res = await axios.get(
        `${API}/quotes/${activeId}/einvoice.xml?flavour=${flavour}`,
        { responseType: "blob" }
      );
      const label = flavour === "xrechnung" ? "XRechnung" : "ZUGFeRD";
      triggerDownload(res.data, `${label}_${quote.angebot_nr || "rechnung"}.xml`, "application/xml");
      toast.success(`${label}-XML heruntergeladen`, { id: "einv" });
      setEInvoiceOpen(false);
    } catch (e) {
      toast.error("E-Rechnung konnte nicht erzeugt werden", { id: "einv" });
    }
  };

  const exportZugferdPdf = async () => {
    const ok = await checkEInvoice();
    if (!ok) return;
    toast.loading("ZUGFeRD-PDF wird erzeugt …", { id: "einv" });
    try {
      const finalBytes = await buildPdfBytes();
      // bytes -> base64
      const b64 = bytesToBase64(finalBytes);
      const res = await axios.post(
        `${API}/quotes/${activeId}/einvoice/zugferd-pdf`,
        { pdf_base64: b64 },
        { responseType: "blob" }
      );
      triggerDownload(res.data, `ZUGFeRD_${quote.angebot_nr || "rechnung"}.pdf`, "application/pdf");
      toast.success("ZUGFeRD-PDF heruntergeladen", { id: "einv" });
      setEInvoiceOpen(false);
    } catch (e) {
      toast.error("ZUGFeRD-PDF konnte nicht erzeugt werden", { id: "einv" });
    }
  };

  // ---- DATEV-Export ----
  const [datevFrom, setDatevFrom] = useState("");
  const [datevTo, setDatevTo] = useState("");
  const exportDatev = async () => {
    try {
      const params = [];
      if (datevFrom) params.push(`from_date=${datevFrom}`);
      if (datevTo) params.push(`to_date=${datevTo}`);
      const res = await axios.get(
        `${API}/quotes/export/datev${params.length ? `?${params.join("&")}` : ""}`,
        { responseType: "blob" }
      );
      triggerDownload(res.data, "EXTF_Buchungsstapel.csv", "text/csv");
      toast.success("DATEV-CSV heruntergeladen");
    } catch {
      toast.error("DATEV-Export fehlgeschlagen");
    }
  };

  // ---- Steuer / ELSTER (UStVA + ZM) ----
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [taxPType, setTaxPType] = useState("month");
  const [taxVal, setTaxVal] = useState(new Date().getMonth() + 1);
  const [ustva, setUstva] = useState(null);
  const [zm, setZm] = useState(null);
  const [atVat, setAtVat] = useState(null);
  const [chVat, setChVat] = useState(null);
  const [vorsteuer, setVorsteuer] = useState(0);
  const taxParams = () => `year=${taxYear}&period_type=${taxPType}&value=${taxVal}`;
  const fetchTax = async () => {
    try {
      const [u, z, at, ch] = await Promise.all([
        axios.get(`${API}/tax/ustva?${taxParams()}`),
        axios.get(`${API}/tax/zm?year=${taxYear}&period_type=quarter&value=${taxPType === "quarter" ? taxVal : Math.ceil(taxVal / 3)}`),
        axios.get(`${API}/tax/country-vat?country=AT&${taxParams()}`),
        axios.get(`${API}/tax/country-vat?country=CH&${taxParams()}`),
      ]);
      setUstva(u.data);
      setZm(z.data);
      setAtVat(at.data);
      setChVat(ch.data);
    } catch {
      toast.error("Steuerdaten konnten nicht geladen werden");
    }
  };
  useEffect(() => {
    if (tab === "steuer") fetchTax();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, taxYear, taxPType, taxVal]);
  const exportTaxCsv = async (kind) => {
    try {
      const extra = kind === "ustva" ? `&vorsteuer=${vorsteuer || 0}` : "";
      const res = await axios.get(`${API}/tax/${kind}/export.csv?${taxParams()}${extra}`, { responseType: "blob" });
      triggerDownload(res.data, `${kind.toUpperCase()}_${taxYear}_${taxPType}_${taxVal}.csv`, "text/csv");
      toast.success("CSV heruntergeladen");
    } catch {
      toast.error("Export fehlgeschlagen");
    }
  };
  const exportCountryVat = async (country) => {
    try {
      const res = await axios.get(`${API}/tax/country-vat/export.csv?country=${country}&${taxParams()}`, { responseType: "blob" });
      const label = country === "AT" ? "Oesterreich" : "Schweiz";
      triggerDownload(res.data, `Umsatz_${label}_${taxYear}_${taxPType}_${taxVal}.csv`, "text/csv");
      toast.success("CSV heruntergeladen");
    } catch {
      toast.error("Export fehlgeschlagen");
    }
  };
  const taxZahllast = ustva ? Math.round((ustva.ust_total - (parseFloat(vorsteuer) || 0)) * 100) / 100 : 0;
  const fmtEur = (n) => (n || 0).toLocaleString(company?.locale || "de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " " + (company?.base_currency || "EUR");
  const fmtCur = (n, cur) => (n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " " + (cur || "EUR");

  // ---- Benannte Vorlagen ----
  const [namedTemplates, setNamedTemplates] = useState([]);
  const fetchNamedTemplates = async () => {
    try {
      const res = await axios.get(`${API}/named-templates`);
      setNamedTemplates(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };
  const saveNamedTemplate = async () => {
    const name = window.prompt("Name der Vorlage:", quote.leistung_titel || "Meine Vorlage");
    if (!name) return;
    try {
      await axios.post(`${API}/named-templates`, {
        name,
        typ: quote.dokument_typ || "angebot",
        data: {
          leistung_titel: quote.leistung_titel,
          leistung_beschreibung: quote.leistung_beschreibung,
          einleitung: quote.einleitung,
          leistungsdatum: quote.leistungsdatum,
          zahlungsziel: quote.zahlungsziel,
          notizen: quote.notizen,
          attachments: quote.attachments,
          positions: quote.positions,
          include_vertrag: quote.include_vertrag,
          tax_rate: quote.tax_rate,
          country: quote.country,
          currency: quote.currency,
          layout: quote.layout,
        },
      });
      toast.success(`Vorlage „${name}“ gespeichert`);
      fetchNamedTemplates();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };
  const applyNamedTemplate = (tpl) => {
    if (!tpl?.data) return;
    setQuote((q) => ({ ...q, ...tpl.data }));
    toast.success(`Vorlage „${tpl.name}“ geladen`);
  };
  const deleteNamedTemplate = async (id) => {
    if (!window.confirm("Vorlage löschen?")) return;
    try {
      await axios.delete(`${API}/named-templates/${id}`);
      fetchNamedTemplates();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  // ---- Zahlungsübersicht (Dashboard) ----
  const [payDash, setPayDash] = useState(null);
  const [payKunde, setPayKunde] = useState("");
  const fetchPayDash = async (kunde = "") => {
    try {
      const res = await axios.get(
        `${API}/dashboard/payments${kunde ? `?kunde=${encodeURIComponent(kunde)}` : ""}`
      );
      setPayDash(res.data);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    if (tab === "payments") fetchPayDash(payKunde);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, payKunde]);

  // ---- White-Label-Anfragen ----
  const [inquiries, setInquiries] = useState([]);
  const fetchInquiries = async () => {
    try {
      const res = await axios.get(`${API}/whitelabel-inquiries`);
      setInquiries(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };
  const setInquiryStatus = async (id, status) => {
    setInquiries((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await axios.patch(`${API}/whitelabel-inquiries/${id}/status`, { status });
    } catch (e) {
      console.error(e);
      fetchInquiries();
    }
  };
  useEffect(() => {
    if (tab === "anfragen") fetchInquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ---- Landing-Analytics ----
  const [analytics, setAnalytics] = useState(null);
  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/landing-analytics`);
      setAnalytics(res.data);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    if (tab === "marketing") fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ---- Stripe Connect (Partner-Auszahlungen) ----
  const [connect, setConnect] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const fetchConnect = async () => {
    try {
      const res = await axios.get(`${API}/connect/status`);
      setConnect(res.data);
    } catch (e) { /* ignore */ }
  };
  const startConnectOnboard = async () => {
    setConnectLoading(true);
    try {
      const res = await axios.post(`${API}/connect/onboard`, { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connect-Onboarding fehlgeschlagen");
      setConnectLoading(false);
    }
  };
  const openConnectDashboard = async () => {
    try {
      const res = await axios.post(`${API}/connect/login-link`, {});
      window.open(res.data.url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Stripe-Dashboard nicht öffnen");
    }
  };

  // ---- Plattform-Übersicht (nur Plattformbetreiber) ----
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const canManageWhiteLabel = isPlatformOwner || subscription?.license === "whitelabel";
  const [platform, setPlatform] = useState(null);
  const fetchPlatform = async () => {
    try {
      const res = await axios.get(`${API}/platform/overview`);
      setPlatform(res.data);
      toast.success("Plattformdaten aktualisiert");
      setIsPlatformOwner(true);
    } catch (e) {
      if (e.response?.status !== 403) console.error(e);
    }
  };
  // ---- Gratis-Zugang verwalten (Vertriebspartner / geschenkte Konten) ----
  const [compAccounts, setCompAccounts] = useState([]);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantPlan, setGrantPlan] = useState("business");
  const [grantBusy, setGrantBusy] = useState(false);
  const fetchCompAccounts = async () => {
    try {
      const res = await axios.get(`${API}/platform/comp-accounts`);
      setCompAccounts(res.data.accounts || []);
    } catch (e) {
      if (e.response?.status !== 403) console.error(e);
    }
  };
  const grantAccess = async () => {
    const email = (grantEmail || "").trim();
    if (!email) return;
    setGrantBusy(true);
    try {
      const isAll = grantPlan === "business_all";
      const res = await axios.post(`${API}/platform/grant-access`, {
        email,
        plan: isAll ? "business" : grantPlan,
        ...(isAll ? { modules: { crm: "premium", travel: "premium", workforce: "premium", expense_studio: "premium", commerce: "premium" } } : {}),
        origin_url: window.location.origin,
      });
      const label = isAll ? "Business + alle Module" : grantPlan;
      if (res.data?.email_sent) {
        toast.success(`Zugang freigeschaltet (${label}) – Willkommens-E-Mail an ${email} gesendet.`);
      } else {
        toast.success(`Zugang freigeschaltet: ${email} (${label}). Hinweis: keine E-Mail versendet (Resend nicht konfiguriert).`);
      }
      setGrantEmail("");
      fetchCompAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Freischaltung fehlgeschlagen");
    } finally {
      setGrantBusy(false);
    }
  };
  const revokeAccess = async (email) => {
    if (!window.confirm(`Zugang für ${email} wirklich entziehen?`)) return;
    try {
      await axios.post(`${API}/platform/revoke-access`, { email });
      toast.success(`Zugang entzogen: ${email}`);
      fetchCompAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Entziehen fehlgeschlagen");
    }
  };
  const updateCompRights = async ({ email, plan, modules, access_expires_on }) => {
    await axios.put(`${API}/platform/comp-accounts/rights`, {
      email,
      plan,
      modules,
      access_expires_on: access_expires_on || null,
    });
    await fetchCompAccounts();
  };
  // ---- Ablefy (Merchant of Record) – Verkäufe & Tarif-Zuordnung ----
  const [ablefy, setAblefy] = useState(null);
  const [mapProductId, setMapProductId] = useState("");
  const [mapPlan, setMapPlan] = useState("pro");
  const [mapName, setMapName] = useState("");
  const [mapCheckoutUrl, setMapCheckoutUrl] = useState("");
  const [mapBillingInterval, setMapBillingInterval] = useState("month");
  const [workshopBundleUrl, setWorkshopBundleUrl] = useState("");
  const [workshopBundleInterval, setWorkshopBundleInterval] = useState("month");
  const [mapBusy, setMapBusy] = useState(false);
  const fetchAblefy = async () => {
    try {
      const res = await axios.get(`${API}/platform/ablefy`);
      setAblefy(res.data);
    } catch (e) {
      if (e.response?.status !== 403) console.error(e);
    }
  };
  const saveAblefyMap = async () => {
    const pid = (mapProductId || "").trim();
    if (!pid) { toast.error("Bitte Produkt-ID angeben"); return; }
    setMapBusy(true);
    try {
      await axios.post(`${API}/platform/ablefy/map`, {
        product_id: pid,
        plan: mapPlan,
        product_name: mapName,
        checkout_url: mapCheckoutUrl,
        billing_interval: mapBillingInterval,
      });
      toast.success(`Zuordnung gespeichert: ${pid} → ${mapPlan}`);
      setMapProductId(""); setMapName(""); setMapCheckoutUrl(""); setMapBillingInterval("month");
      fetchAblefy();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setMapBusy(false);
    }
  };
  const deleteAblefyMap = async (pid) => {
    try {
      await axios.delete(`${API}/platform/ablefy/map/${encodeURIComponent(pid)}`);
      fetchAblefy();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };
  const saveWorkshopBundleLink = async () => {
    const checkoutUrl = (workshopBundleUrl || "").trim();
    if (!checkoutUrl) { toast.error("Bitte den Ablefy-Bezahllink einfügen"); return; }
    setMapBusy(true);
    try {
      await axios.put(`${API}/platform/ablefy/workshop-bundle`, {
        checkout_url: checkoutUrl,
        billing_interval: workshopBundleInterval,
      });
      setWorkshopBundleUrl("");
      toast.success(`Werkstatt-Bundle ${workshopBundleInterval === "year" ? "jährlich" : "monatlich"} mit Ablefy verknüpft`);
      fetchAblefy();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verknüpfung konnte nicht gespeichert werden");
    } finally {
      setMapBusy(false);
    }
  };
  const ablefyWebhookUrl = ablefy ? `${BACKEND_URL}${ablefy.webhook_path}?token=${ablefy.webhook_token}` : "";

  useEffect(() => {
    fetchConnect();
    fetchPlatform();
    fetchCompAccounts();
    fetchAblefy();
    // handle Connect return
    const p = new URLSearchParams(window.location.search);
    if (p.get("connect") === "done") {
      toast.success("Stripe Connect verbunden – Status wird aktualisiert.");
      setTimeout(fetchConnect, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openQuoteById = async (id) => {
    let q = quotes.find((x) => x.id === id);
    if (!q) {
      try {
        q = (await axios.get(`${API}/quotes/${id}`)).data;
        loadQuotes();
      } catch { return; }
    }
    if (q) {
      setActiveId(q.id);
      setQuote(q);
      setTab("editor");
    }
  };

  const [reminderBusy, setReminderBusy] = useState("");
  const sendReminder = async (id) => {
    setReminderBusy(id);
    try {
      await axios.post(`${API}/quotes/${id}/send-reminder`, {
        origin_url: window.location.origin,
      });
      toast.success("Zahlungserinnerung gesendet");
      fetchPayDash(payKunde);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erinnerung konnte nicht gesendet werden");
    } finally {
      setReminderBusy("");
    }
  };

  return (
    <CurrencyProvider company={company}>
    <div className="min-h-screen bg-[var(--brand-bg)] font-[Inter] pb-24 md:pb-0">
      <Toaster richColors position="top-right" />

      {adminUser?.sandbox_demo && (
        <div className="bg-amber-700 text-white text-sm px-4 py-2.5 flex items-center justify-center gap-x-4 gap-y-1 flex-wrap" data-testid="sandbox-demo-banner">
          <span className="flex items-center gap-2 font-medium">
            <FlaskConical className="w-4 h-4" /> Persönliche Sandbox-Demo · Eigene Testdaten sind nur in diesem Testbereich sichtbar und werden nach Ablauf gelöscht.
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setSandboxDeleteOpen(true)}
            className="border-white/70 bg-transparent text-white hover:bg-white hover:text-amber-800"
            data-testid="sandbox-demo-delete"
          >
            Sandbox und Testdaten löschen
          </Button>
        </div>
      )}
      {(adminUser?.readonly || isDemoReadonly()) && (
        <div className="bg-[var(--brand-ink)] text-white text-sm px-4 py-2.5 flex items-center justify-center gap-x-4 gap-y-1 flex-wrap" data-testid="demo-banner">
          <span className="flex items-center gap-2 font-medium">
            <Rocket className="w-4 h-4" /> Demo-Modus · Vorführ-Version mit Beispieldaten (schreibgeschützt)
          </span>
          {company?.demo_cta_url && (
          <a
            href={company.demo_cta_url}
            target={(company.demo_cta_url || "").startsWith("http") ? "_blank" : undefined}
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-full bg-[var(--brand-primary)] px-3 py-1 font-semibold text-white hover:opacity-90 transition"
            data-testid="demo-cta"
          >
            <ShoppingCart className="w-3.5 h-3.5" /> {company?.demo_cta_label || "Diese Lösung kaufen"}
          </a>
          )}
        </div>
      )}

      <header className="border-b border-[var(--brand-border)] bg-[var(--brand-bg)]">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <CompanyLogo className="h-12 w-12" />
            <div>
              <div className="font-[Playfair_Display] text-xl font-bold tracking-wide text-[var(--brand-ink)]" data-testid="brand-company-name">
                {FIRMA.firma || FIRMA.name || "Mein Unternehmen"}
              </div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[var(--brand-primary)]">
                Business Studio Suite
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <WorkspaceSwitcher activeOrgId={adminUser?.org_id} />
            <GlobalSearch setTab={setTab} />
              <NotificationsBell
              notifications={notifications}
              unread={unread}
              markAllRead={markAllRead}
              fetchNotifications={fetchNotifications}
              loadOne={loadOne}
              setTab={setTab}
            />

            {hasBilling && (
            <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => newQuote("angebot")}
              className="border-[var(--brand-primary)] text-[var(--brand-ink)] hover:bg-[var(--brand-border-light)]"
              data-testid="new-quote-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Neues Angebot
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => newQuote("rechnung")}
              className="border-[var(--brand-ink)] text-[var(--brand-ink)] hover:bg-[var(--brand-border-light)]"
              data-testid="new-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Neue Rechnung
            </Button>
            <Button
              size="sm"
              onClick={save}
              className="hidden md:inline-flex bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
              data-testid="save-quote-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              {activeId ? "Aktualisieren" : "Speichern"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={saveAsTemplate}
              className="hidden md:inline-flex border-[var(--brand-border)]"
              data-testid="save-template-btn"
              title="Aktuelle Felder als Standardvorlage speichern"
            >
              <FileText className="w-4 h-4 mr-1" /> Vorlage
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={sendByEmail}
              disabled={!activeId || !emailCfg.enabled}
              className="hidden md:inline-flex border-[var(--brand-border)]"
              data-testid="send-email-btn"
              title={emailCfg.enabled ? "Per E-Mail an Kunde senden" : "E-Mail-Versand in Einstellungen aktivieren"}
            >
              <Send className="w-4 h-4 mr-1" /> E-Mail
            </Button>
            <Button
              size="sm"
              onClick={downloadPdf}
              className="hidden md:inline-flex bg-[var(--brand-ink)] hover:bg-black text-white"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
            {INVOICE_TYPES.includes(quote.dokument_typ) && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => { await checkEInvoice(); setEInvoiceOpen(true); }}
                disabled={!activeId}
                className="hidden md:inline-flex border-[var(--brand-primary)] text-[var(--brand-ink)] hover:bg-[var(--brand-border-light)]"
                data-testid="einvoice-btn"
                title="E-Rechnung (EN 16931) exportieren"
              >
                <FileText className="w-4 h-4 mr-1" /> E-Rechnung
              </Button>
            )}
            </>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setPwOpen(true)}
              className="border-[var(--brand-border)]"
              data-testid="open-change-pw"
              title="Passwort ändern"
            >
              <KeyRound className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onLogout}
              className="border-[var(--brand-border)]"
              data-testid="logout-btn"
              title="Abmelden"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {eInvoiceOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
          onClick={() => setEInvoiceOpen(false)}
          data-testid="einvoice-modal"
        >
          <Card
            className="p-6 bg-white border-[var(--brand-border)] w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-[Playfair_Display] text-xl font-semibold mb-1">
              E-Rechnung exportieren
            </h3>
            <p className="text-sm text-[var(--brand-muted)] mb-4">
              Nach Norm EN 16931 (B2B-pflichtkonform für DE/AT).
            </p>

            {eInvoiceMissing.length > 0 ? (
              <div
                className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 space-y-1"
                data-testid="einvoice-missing"
              >
                <div className="font-semibold">Bitte zuerst ergänzen:</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  {eInvoiceMissing.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
                <div className="pt-2 text-xs text-red-600">
                  Firmen-/Steuerdaten unter „Einstellungen“, Kundendaten im Editor.
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Button
                  onClick={exportZugferdPdf}
                  className="w-full justify-start bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
                  data-testid="einvoice-zugferd-pdf"
                >
                  <Download className="w-4 h-4 mr-2" />
                  ZUGFeRD-PDF (PDF + eingebettetes XML)
                </Button>
                <Button
                  variant="outline"
                  onClick={() => exportEInvoiceXml("xrechnung")}
                  className="w-full justify-start border-[var(--brand-border)]"
                  data-testid="einvoice-xrechnung-xml"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  XRechnung (reines XML)
                </Button>
                <Button
                  variant="outline"
                  onClick={() => exportEInvoiceXml("zugferd")}
                  className="w-full justify-start border-[var(--brand-border)]"
                  data-testid="einvoice-zugferd-xml"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  ZUGFeRD-XML (nur XML)
                </Button>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button variant="outline" size="sm" onClick={() => setEInvoiceOpen(false)}>
                Schließen
              </Button>
            </div>
          </Card>
        </div>
      )}

      {autoSigOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
          onClick={() => setAutoSigOpen(false)}
        >
          <Card
            className="p-6 bg-white border-[var(--brand-border)] w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-[Playfair_Display] text-xl font-semibold mb-3">
              Signatur-Block erstellen
            </h3>
            <div className="space-y-3">
              <div>
                <Label>Name (in Handschrift)</Label>
                <Input
                  value={autoSigName}
                  onChange={(e) => setAutoSigName(e.target.value)}
                  data-testid="auto-sig-name"
                />
              </div>
              <div>
                <Label>Spruch / Claim</Label>
                <Input
                  value={autoSigTagline}
                  onChange={(e) => setAutoSigTagline(e.target.value)}
                  data-testid="auto-sig-tagline"
                />
              </div>
              <div>
                <Label>URLs (eine pro Zeile)</Label>
                <Textarea
                  rows={2}
                  value={autoSigUrls}
                  onChange={(e) => setAutoSigUrls(e.target.value)}
                  data-testid="auto-sig-urls"
                />
              </div>
              <div>
                <Label>Handschrift-Stil</Label>
                <div className="grid grid-cols-1 gap-2 max-h-[300px] overflow-auto">
                  {[
                    { name: "Sail", size: 44, hint: "Canva-Style elegant" },
                    { name: "Birthstone", size: 40, hint: "≈ Amsterdam Two" },
                    { name: "Mea Culpa", size: 38, hint: "≈ Amsterdam Two" },
                    { name: "Imperial Script", size: 38, hint: "geschwungen breit" },
                    { name: "Bonheur Royale", size: 38, hint: "charmant" },
                    { name: "Style Script", size: 36, hint: "lebendig" },
                    { name: "Send Flowers", size: 34, hint: "kräftig" },
                    { name: "Italianno", size: 44, hint: "elegant dünn" },
                    { name: "Petit Formal Script", size: 26, hint: "formal" },
                    { name: "Mrs Saint Delafield", size: 36, hint: "edel" },
                    { name: "Tangerine", size: 46, hint: "fein romantisch" },
                    { name: "Dr Sugiyama", size: 38, hint: "klassisch" },
                    { name: "Parisienne", size: 32, hint: "rund" },
                  ].map((f) => (
                    <button
                      key={f.name}
                      type="button"
                      onClick={() => setAutoSigFont(f.name)}
                      className={`text-left px-4 py-3 rounded border transition ${
                        autoSigFont === f.name
                          ? "border-[var(--brand-primary)] bg-[var(--brand-bg)]"
                          : "border-[var(--brand-border)] bg-white hover:bg-[var(--brand-bg)]"
                      }`}
                      data-testid={`auto-sig-font-${f.name.replace(/\s+/g, "-")}`}
                    >
                      <span
                        style={{
                          fontFamily: `'${f.name}', cursive`,
                          fontSize: f.size,
                        }}
                      >
                        {autoSigName || company?.owner_name || company?.company_name || "Ihr Name"}
                      </span>
                      <div className="text-[11px] text-[var(--brand-muted)] mt-1">
                        {f.name} · {f.hint}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAutoSigOpen(false)}
                >
                  Abbrechen
                </Button>
                <Button
                  size="sm"
                  onClick={saveAutoSignature}
                  className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
                  data-testid="auto-sig-save"
                >
                  Speichern
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {sigPadOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
          onClick={() => setSigPadOpen(false)}
        >
          <Card
            className="p-6 bg-white border-[var(--brand-border)] w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-[Playfair_Display] text-xl font-semibold mb-3">
              Unterschrift zeichnen
            </h3>
            <p className="text-xs text-[var(--brand-muted)] mb-3">
              Zeichne mit Maus oder Finger. Du kannst sie später jederzeit neu
              zeichnen.
            </p>
            <SignaturePad onChange={setSigDraft} />
            <div className="flex gap-2 justify-end mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSigPadOpen(false)}
              >
                Abbrechen
              </Button>
              <Button
                size="sm"
                onClick={saveDrawnSignature}
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
                data-testid="btn-save-sig"
              >
                Speichern
              </Button>
            </div>
          </Card>
        </div>
      )}

      {pwOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
          onClick={() => setPwOpen(false)}
        >
          <Card
            className="p-6 bg-white border-[var(--brand-border)] w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-[Playfair_Display] text-xl font-semibold mb-4">
              Passwort ändern
            </h3>
            <div className="space-y-3">
              <div>
                <Label>Aktuelles Passwort</Label>
                <Input
                  type="password"
                  value={pwOld}
                  onChange={(e) => setPwOld(e.target.value)}
                  data-testid="pw-old"
                />
              </div>
              <div>
                <Label>Neues Passwort (min. 8 Zeichen)</Label>
                <Input
                  type="password"
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                  data-testid="pw-new"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPwOpen(false)}
                >
                  Abbrechen
                </Button>
                <Button
                  size="sm"
                  disabled={pwLoad}
                  onClick={changePassword}
                  className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
                  data-testid="pw-save"
                >
                  {pwLoad ? "Wird gespeichert…" : "Speichern"}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {sandboxDeleteOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
          onClick={() => !sandboxDeleteBusy && setSandboxDeleteOpen(false)}
        >
          <Card
            className="p-6 bg-white border-[var(--brand-border)] w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="sandbox-delete-dialog"
          >
            <h3 className="font-[Playfair_Display] text-xl font-semibold">Sandbox und Testdaten löschen</h3>
            <p className="mt-2 text-sm leading-6 text-[var(--brand-muted)]">
              Du löschst dein eigenes Sandbox-Konto und alle Testdaten sofort. Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="mt-5 space-y-3">
              <div>
                <Label>Aktuelles Passwort</Label>
                <Input type="password" value={sandboxDeletePassword} onChange={(e) => setSandboxDeletePassword(e.target.value)} data-testid="sandbox-delete-password" />
              </div>
              <div>
                <Label>Zur Bestätigung LÖSCHEN eingeben</Label>
                <Input value={sandboxDeleteConfirmation} onChange={(e) => setSandboxDeleteConfirmation(e.target.value)} data-testid="sandbox-delete-confirmation" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setSandboxDeleteOpen(false)} disabled={sandboxDeleteBusy}>Abbrechen</Button>
                <Button size="sm" disabled={sandboxDeleteBusy} onClick={deleteSandbox} className="bg-red-700 hover:bg-red-800 text-white" data-testid="sandbox-delete-confirm">
                  {sandboxDeleteBusy ? "Wird gelöscht…" : "Jetzt endgültig löschen"}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      <main className="max-w-[1400px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="hidden md:flex md:flex-wrap h-auto justify-start gap-1 bg-[var(--brand-border-light)] border border-[var(--brand-border)]">
            <TabsTrigger
              value="uebersicht"
              data-testid="tab-uebersicht"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <LayoutDashboard className="w-4 h-4 mr-1" /> Übersicht
            </TabsTrigger>
            <TabsTrigger value="cockpit" data-testid="tab-cockpit" className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]">
              <TrendingUp className="w-4 h-4 mr-1" /> Business Cockpit
            </TabsTrigger>
            <TabsTrigger value="meintag" data-testid="tab-meintag" className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]">
              <CalendarDays className="w-4 h-4 mr-1" /> Mein Tag
            </TabsTrigger>
            <TabsTrigger value="automationen" data-testid="tab-automationen" className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]">
              <Zap className="w-4 h-4 mr-1" /> Automationen
            </TabsTrigger>
            {hasBilling ? (
            <>
            <TabsTrigger
              value="editor"
              data-testid="tab-editor"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <Pencil className="w-4 h-4 mr-1" /> Editor & Vorschau
            </TabsTrigger>
            <TabsTrigger
              value="list"
              data-testid="tab-list"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <FileText className="w-4 h-4 mr-1" /> Angebote ({quotes.length})
            </TabsTrigger>
            </>
            ) : (!isMitarbeiter && (
              <LockedTab label="Angebote & Rechnungen" icon={FileText} testId="locked-tab-billing" onClick={() => setUpsellModule("billing")} />
            ))}
            {hasFeature("dashboard") && (
            <TabsTrigger
              value="payments"
              data-testid="tab-payments"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <Wallet className="w-4 h-4 mr-1" /> Zahlungen
            </TabsTrigger>
            )}
            {isPlatformOwner && (
            <TabsTrigger
              value="anfragen"
              data-testid="tab-anfragen"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <Mail className="w-4 h-4 mr-1" /> Anfragen
            </TabsTrigger>
            )}
            {hasFeature("crm") && (
            <TabsTrigger
              value="marketing"
              data-testid="tab-marketing"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <TrendingUp className="w-4 h-4 mr-1" /> Marketing
            </TabsTrigger>
            )}
            {isPlatformOwner && (
              <TabsTrigger
                value="platform"
                data-testid="tab-platform"
                className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
              >
                <Briefcase className="w-4 h-4 mr-1" /> Plattform
              </TabsTrigger>
            )}
            {/* Zeilenumbruch: zweite Tab-Reihe ab Steuer/ELSTER */}
            <span className="basis-full h-0" aria-hidden="true" />
            {hasFeature("datev") && (
            <TabsTrigger
              value="steuer"
              data-testid="tab-steuer"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <FileText className="w-4 h-4 mr-1" /> Steuer/ELSTER
            </TabsTrigger>
            )}
            {hasFeature("dashboard") && (
            <TabsTrigger
              value="ausgaben"
              data-testid="tab-ausgaben"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <Receipt className="w-4 h-4 mr-1" /> Betriebsausgaben
            </TabsTrigger>
            )}
            {hasFeature("crm") ? (
            <TabsTrigger
              value="crm"
              data-testid="tab-crm"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <Briefcase className="w-4 h-4 mr-1" /> CRM
            </TabsTrigger>
            ) : (!isMitarbeiter && (
              <LockedTab label="CRM" icon={Briefcase} testId="locked-tab-crm" onClick={() => setUpsellModule("crm")} />
            ))}
            {hasFeature("crm") && (
            <TabsTrigger
              value="kunden"
              data-testid="tab-kunden"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <Users className="w-4 h-4 mr-1" /> Kunden
            </TabsTrigger>
            )}
            {hasFeature("reisestudio") ? (
            <TabsTrigger
              value="reise"
              data-testid="tab-reise"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <Plane className="w-4 h-4 mr-1" /> Business Travel Studio
            </TabsTrigger>
            ) : (!isMitarbeiter && (
              <LockedTab label="Business Travel Studio" icon={Plane} testId="locked-tab-reise" onClick={() => setUpsellModule("travel")} />
            ))}
            {hasFeature("workforce") && (
            <TabsTrigger
              value="workforce"
              data-testid="tab-workforce"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <Users className="w-4 h-4 mr-1" /> Project & Workforce
            </TabsTrigger>
            )}
            {hasFeature("calendar") && (
            <TabsTrigger
              value="kalender"
              data-testid="tab-kalender"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <CalendarDays className="w-4 h-4 mr-1" /> Kalender
            </TabsTrigger>
            )}
            {hasFeature("expense_studio") && (
            <TabsTrigger value="expense_studio" data-testid="tab-expense-studio" className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]">
              <Receipt className="w-4 h-4 mr-1" /> Expense & Document
            </TabsTrigger>
            )}
            {hasFeature("commerce") && (
            <TabsTrigger value="commerce" data-testid="tab-commerce" className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]">
              <ShoppingCart className="w-4 h-4 mr-1" /> Commerce
            </TabsTrigger>
            )}
            {hasFeature("lead_funnel") ? (
            <TabsTrigger value="lead_funnel" data-testid="tab-lead-funnel" className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]">
              <Rocket className="w-4 h-4 mr-1" /> Lead & Funnel
            </TabsTrigger>
            ) : (!isMitarbeiter && (
              <LockedTab label="Lead & Funnel" icon={Rocket} testId="locked-tab-lead-funnel" onClick={() => setUpsellModule("lead_funnel")} />
            ))}
            <TabsTrigger value="import_export" data-testid="tab-import-export" className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]">
              <FileText className="w-4 h-4 mr-1" /> Import/Export
            </TabsTrigger>
            <TabsTrigger value="freigaben" data-testid="tab-freigaben" className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]">
              <Shield className="w-4 h-4 mr-1" /> Freigaben
            </TabsTrigger>
            <TabsTrigger value="einrichtung" data-testid="tab-einrichtung" className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]">
              <Rocket className="w-4 h-4 mr-1" /> Einrichtung
            </TabsTrigger>
            {(hasFeature("crm") || hasFeature("expense_studio")) && (
            <TabsTrigger value="profile360" data-testid="tab-profile360" className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]">
              <Users className="w-4 h-4 mr-1" /> 360° Profile
            </TabsTrigger>
            )}
            {canManageWhiteLabel && (
            <TabsTrigger value="whitelabel" data-testid="tab-whitelabel" className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]">
              <Palette className="w-4 h-4 mr-1" /> White-Label
            </TabsTrigger>
            )}
            {isPnpUser && (
            <TabsTrigger value="pnp" data-testid="tab-pnp" className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]">
              <Briefcase className="w-4 h-4 mr-1" /> P & P Studio
            </TabsTrigger>
            )}
            {!isMitarbeiter && (
            <TabsTrigger
              value="settings"
              data-testid="tab-settings"
              className="data-[state=active]:bg-white data-[state=active]:text-[var(--brand-ink)]"
            >
              <ShieldCheck className="w-4 h-4 mr-1" /> Einstellungen
            </TabsTrigger>
            )}
          </TabsList>

          {/* ----- Übersicht / Dashboard ----- */}
          <TabsContent value="uebersicht" className="mt-6">
            <DashboardTab company={company} setTab={setTab} openQuoteById={openQuoteById} />
          </TabsContent>
          <TabsContent value="meintag" className="mt-6">
            <MeinTagView setTab={setTab} />
          </TabsContent>
          <TabsContent value="cockpit" className="mt-6">
            <BusinessCockpit hasFeature={hasFeature} setTab={setTab} />
          </TabsContent>
          <TabsContent value="automationen" className="mt-6">
            <AutomationsView />
          </TabsContent>
          <TabsContent value="import_export" className="mt-6">
            <ImportExportView />
          </TabsContent>
          <TabsContent value="freigaben" className="mt-6">
            <ApprovalView />
          </TabsContent>
          <TabsContent value="einrichtung" className="mt-6">
            <OnboardingAssistant />
          </TabsContent>
          <TabsContent value="profile360" className="mt-6">
            <Profile360Hub />
          </TabsContent>
          <TabsContent value="whitelabel" className="mt-6">
            <WhiteLabelCentral />
          </TabsContent>
          {isPnpUser && (
          <TabsContent value="pnp" className="mt-6">
            <PnpStudio onOpenQuote={openQuoteById} />
          </TabsContent>
          )}

          {/* ----- Editor ----- */}
          <TabsContent value="editor" className="mt-6">
            <EditorTab
              quote={quote} activeId={activeId} total={total} payCfg={payCfg}
              company={company || companyForm} customers={customers} packages={packages} attachments={attachments}
              signature={signature} steuer={steuer} namedTemplates={namedTemplates}
              previewRef={previewRef} COUNTRY_PRESETS={COUNTRY_PRESETS}
              setTab={setTab} hasFeature={hasFeature}
              toggleKleinunternehmer={toggleKleinunternehmer}
              switchDokumentTyp={switchDokumentTyp} convertToInvoice={convertToInvoice}
              convertTo={convertTo} update={update} fetchNextNumber={fetchNextNumber}
              copyTracking={copyTracking} applyCustomer={applyCustomer}
              addCustomPosition={addCustomPosition} addPaket={addPaket}
              updatePosition={updatePosition} removePosition={removePosition}
              toggleAttachment={toggleAttachment} applyNamedTemplate={applyNamedTemplate}
              saveNamedTemplate={saveNamedTemplate} deleteNamedTemplate={deleteNamedTemplate}
              onReloadQuote={loadOne} onOpenGeneratedInvoice={loadOne}
            />
          </TabsContent>

          {/* ----- List ----- */}
          <TabsContent value="list" className="mt-6">
            <AngeboteTab company={company || companyForm} quotes={quotes} datevFrom={datevFrom} setDatevFrom={setDatevFrom} datevTo={datevTo} setDatevTo={setDatevTo} loadOne={loadOne} convertOneToInvoice={convertOneToInvoice} exportDatev={exportDatev} changeStatus={changeStatus} duplicateOne={duplicateOne} removeOne={removeOne} />
          </TabsContent>

          {/* ----- Settings (Firmendaten, Pakete, Dokumente, Zahlung, E-Mail) ----- */}
          {/* ----- CRM (Deal-Pipeline) ----- */}
          <TabsContent value="crm" className="mt-6">
              <CRMTab company={company || companyForm} onCreateQuoteFromDeal={createQuoteFromDeal} onOpenQuote={openQuoteById} hasFeature={hasFeature} />
          </TabsContent>
          {/* ----- Kunden ----- */}
          <TabsContent value="kunden" className="mt-6">
            <KundenTab
              company={company || companyForm}
              openQuoteById={openQuoteById}
              onNewQuoteForCustomer={newQuoteForCustomer}
              onNewInvoiceForCustomer={newInvoiceForCustomer}
            />
          </TabsContent>
          {/* ----- Geschäftsreise Studio ----- */}
          <TabsContent value="reise" className="mt-6">
            <ReiseStudioTab adminUser={adminUser} openQuoteById={openQuoteById} hasFeature={hasFeature} />
          </TabsContent>
          {/* ----- Project & Workforce Studio ----- */}
          <TabsContent value="workforce" className="mt-6">
            <WorkforceTab />
          </TabsContent>
          <TabsContent value="expense_studio" className="mt-6">
            <ExpenseDocumentTab />
          </TabsContent>
          <TabsContent value="commerce" className="mt-6">
            <CommerceTab />
          </TabsContent>
          <TabsContent value="lead_funnel" className="mt-6">
            <LeadFunnelTab hasFeature={hasFeature} onCreateQuoteFromLead={createQuoteFromFunnelLead} />
          </TabsContent>
          {/* ----- Kalender ----- */}
          <TabsContent value="kalender" className="mt-6">
            <CalendarView />
          </TabsContent>
          {/* ----- Zahlungen (Dashboard) ----- */}
          <TabsContent value="payments" className="mt-6">
            <PaymentsTab payKunde={payKunde} setPayKunde={setPayKunde} payDash={payDash} openQuoteById={openQuoteById} reminderBusy={reminderBusy} sendReminder={sendReminder} />
          </TabsContent>

          <TabsContent value="anfragen" className="mt-6">
            <InquiriesTab inquiries={inquiries} fetchInquiries={fetchInquiries} setInquiryStatus={setInquiryStatus} />
          </TabsContent>

          <TabsContent value="marketing" className="mt-6">
            <MarketingTab analytics={analytics} fetchAnalytics={fetchAnalytics} hasFeature={hasFeature} />
          </TabsContent>


          <TabsContent value="steuer" className="mt-6">
            <SteuerTab taxPType={taxPType} setTaxPType={setTaxPType} taxVal={taxVal} setTaxVal={setTaxVal} taxYear={taxYear} setTaxYear={setTaxYear} ustva={ustva} vorsteuer={vorsteuer} setVorsteuer={setVorsteuer} taxZahllast={taxZahllast} zm={zm} atVat={atVat} chVat={chVat} fetchTax={fetchTax} exportTaxCsv={exportTaxCsv} exportCountryVat={exportCountryVat} />
          </TabsContent>
          <TabsContent value="ausgaben" className="mt-6">
            <BetriebsausgabenTab />
          </TabsContent>

          {isPlatformOwner && (
          <TabsContent value="platform" className="mt-6">
            <PlatformTab platform={platform} ablefy={ablefy} ablefyWebhookUrl={ablefyWebhookUrl} grantEmail={grantEmail} setGrantEmail={setGrantEmail} grantPlan={grantPlan} setGrantPlan={setGrantPlan} grantBusy={grantBusy} grantAccess={grantAccess} compAccounts={compAccounts} revokeAccess={revokeAccess} updateCompRights={updateCompRights} fetchPlatform={fetchPlatform} fetchAblefy={fetchAblefy} mapProductId={mapProductId} setMapProductId={setMapProductId} mapName={mapName} setMapName={setMapName} mapPlan={mapPlan} setMapPlan={setMapPlan} mapCheckoutUrl={mapCheckoutUrl} setMapCheckoutUrl={setMapCheckoutUrl} mapBillingInterval={mapBillingInterval} setMapBillingInterval={setMapBillingInterval} workshopBundleUrl={workshopBundleUrl} setWorkshopBundleUrl={setWorkshopBundleUrl} workshopBundleInterval={workshopBundleInterval} setWorkshopBundleInterval={setWorkshopBundleInterval} mapBusy={mapBusy} saveWorkshopBundleLink={saveWorkshopBundleLink} saveAblefyMap={saveAblefyMap} deleteAblefyMap={deleteAblefyMap} />
          </TabsContent>
          )}


          {!isMitarbeiter && (
          <TabsContent value="settings" className="mt-6">
            <SettingsTab subscription={subscription} company={company} hasFeature={hasFeature} companyForm={companyForm} setC={setC} setCompanyColor={setCompanyColor} saveCompany={saveCompany} onCompanyLogo={onCompanyLogo} connect={connect} connectLoading={connectLoading} startConnectOnboard={startConnectOnboard} openConnectDashboard={openConnectDashboard} payCfg={payCfg} payForm={payForm} setPayForm={setPayForm} savePayment={savePayment} emailCfg={emailCfg} emailForm={emailForm} setEmailForm={setEmailForm} saveEmail={saveEmail} remindersForm={remindersForm} setRemindersForm={setRemindersForm} remindersSaving={remindersSaving} saveReminders={saveReminders} runPredueNow={runPredueNow} runFollowupNow={runFollowupNow} datev={datev} setDatev={setDatev} saveDatev={saveDatev} steuer={steuer} setSteuer={setSteuer} saveSteuer={saveSteuer} packages={packages} newPkg={newPkg} setNewPkg={setNewPkg} createPackage={createPackage} deletePackage={deletePackage} attachments={attachments} fileInputRefs={fileInputRefs} uploadAttachment={uploadAttachment} removeAttachment={removeAttachment} renameAttachment={renameAttachment} signature={signature} removeSignature={removeSignature} uploadSignatureFile={uploadSignatureFile} setSigDraft={setSigDraft} setSigPadOpen={setSigPadOpen} setAutoSigOpen={setAutoSigOpen} openBillingPortal={openBillingPortal} cancelSubscription={cancelSubscription} resumeSubscription={resumeSubscription} />
          </TabsContent>
          )}
        </Tabs>
      </main>

      {/* ------- Mobile Bottom Navigation + Action Bar ------- */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[var(--brand-border)] shadow-[0_-4px_12px_rgba(0,0,0,0.06)]"
        data-testid="mobile-bottom-bar"
      >
        {/* Quick actions row – only visible inside editor tab */}
        {tab === "editor" && (
          <div className="flex items-center gap-2 px-3 pt-2 pb-1 border-b border-[var(--brand-border-light)]">
            <Button
              onClick={save}
              className="flex-1 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white h-11"
              data-testid="mobile-save-btn"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {activeId ? "Aktualisieren" : "Speichern"}
            </Button>
            <Button
              onClick={downloadPdf}
              className="flex-1 bg-[var(--brand-ink)] hover:bg-black text-white h-11"
              data-testid="mobile-pdf-btn"
            >
              <Download className="w-4 h-4 mr-1.5" /> PDF
            </Button>
          </div>
        )}
        {/* Bottom navigation tabs */}
        <div className={`grid ${(hasBilling ? 2 : 1) + 1 + (isMitarbeiter ? 0 : 1) === 4 ? "grid-cols-4" : (hasBilling ? 2 : 1) + 1 + (isMitarbeiter ? 0 : 1) === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
          {[
            hasBilling && { id: "editor", label: "Editor", icon: Pencil, action: () => { setTab("editor"); setMobileMoreOpen(false); } },
            hasBilling && {
              id: "list",
              label: `Angebote${quotes.length ? ` (${quotes.length})` : ""}`,
              icon: FileText,
              action: () => { setTab("list"); setMobileMoreOpen(false); },
            },
            !hasBilling && !isMitarbeiter && {
              id: "__billing-locked", label: "Angebote", icon: FileText,
              action: () => { setMobileMoreOpen(false); setUpsellModule("billing"); },
            },
            {
              id: "__more",
              label: "Mehr",
              icon: Plus,
              action: () => setMobileMoreOpen((o) => !o),
              active: mobileMoreOpen || ["payments", "anfragen", "marketing", "steuer", "platform", "reise", "lead_funnel"].includes(tab),
            },
            !isMitarbeiter && { id: "settings", label: "Einstell.", icon: ShieldCheck, action: () => { setTab("settings"); setMobileMoreOpen(false); } },
          ].filter(Boolean).map(({ id, label, icon: Icon, action, active }) => (
            <button
              key={id}
              type="button"
              onClick={action}
              data-testid={`mobile-tab-${id}`}
              className={`flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium transition ${
                (active !== undefined ? active : tab === id)
                  ? "text-[var(--brand-primary)] bg-[var(--brand-bg)]"
                  : "text-[var(--brand-muted)] hover:bg-[var(--brand-bg)]"
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="truncate max-w-[90%]">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ------- Mobile "Mehr" Bottom Sheet ------- */}
      {mobileMoreOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-more-sheet">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileMoreOpen(false)}
            data-testid="mobile-more-backdrop"
          />
          <div className="absolute bottom-0 left-0 right-0 flex max-h-[calc(100dvh-0.5rem)] flex-col bg-white rounded-t-2xl shadow-2xl">
            <div className="shrink-0 flex items-center justify-between px-5 py-3.5 border-b border-[var(--brand-border-light)]">
              <span className="text-base font-semibold text-[var(--brand-ink)]">Mehr</span>
              <button onClick={() => setMobileMoreOpen(false)} data-testid="mobile-more-close" className="p-1">
                <X className="w-5 h-5 text-[var(--brand-muted)]" />
              </button>
            </div>
            <div
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-2 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]"
              style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
              data-testid="mobile-more-scroll-area"
            >
              {[
                { id: "uebersicht", label: "Übersicht", icon: LayoutDashboard },
                { id: "cockpit", label: "Business Cockpit", icon: TrendingUp },
                { id: "meintag", label: "Mein Tag", icon: CalendarDays },
                { id: "automationen", label: "Automationen", icon: Zap },
                { id: "import_export", label: "Import/Export", icon: FileText },
                { id: "freigaben", label: "Freigaben", icon: Shield },
                { id: "einrichtung", label: "Einrichtung", icon: CheckCircle },
                (hasFeature("crm") || hasFeature("expense_studio")) && { id: "profile360", label: "360° Profile", icon: Users },
                canManageWhiteLabel && { id: "whitelabel", label: "White-Label", icon: Palette },
                isPnpUser && { id: "pnp", label: "P & P Studio", icon: Briefcase },
                hasFeature("dashboard") && { id: "payments", label: "Zahlungen", icon: Wallet },
                hasFeature("crm") ? { id: "crm", label: "CRM", icon: Briefcase } : (!isMitarbeiter && { id: "upsell-crm", label: "CRM", icon: Briefcase, locked: "crm" }),
                hasFeature("crm") && { id: "kunden", label: "Kunden", icon: Users },
                hasFeature("reisestudio") ? { id: "reise", label: "Business Travel Studio", icon: Plane } : (!isMitarbeiter && { id: "upsell-reise", label: "Business Travel Studio", icon: Plane, locked: "travel" }),
                hasFeature("calendar") && { id: "kalender", label: "Kalender", icon: CalendarDays },
                isPlatformOwner && { id: "anfragen", label: "Anfragen", icon: Mail },
                hasFeature("crm") && { id: "marketing", label: "Marketing", icon: TrendingUp },
                hasFeature("datev") && { id: "steuer", label: "Steuer / ELSTER", icon: FileText },
                hasFeature("dashboard") && { id: "ausgaben", label: "Betriebsausgaben", icon: Receipt },
                hasFeature("workforce") && { id: "workforce", label: "Project & Workforce", icon: Users },
                hasFeature("expense_studio") && { id: "expense_studio", label: "Expense & Document", icon: Receipt },
                hasFeature("commerce") && { id: "commerce", label: "Commerce", icon: ShoppingCart },
                hasFeature("lead_funnel") ? { id: "lead_funnel", label: "Lead & Funnel", icon: Rocket } : (!isMitarbeiter && { id: "upsell-lead-funnel", label: "Lead & Funnel", icon: Rocket, locked: "lead_funnel" }),
                isPlatformOwner && { id: "platform", label: "Plattform", icon: Briefcase },
              ]
                .filter(Boolean)
                .map(({ id, label, icon: Icon, locked }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => { if (locked) { setUpsellModule(locked); } else { setTab(id); } setMobileMoreOpen(false); }}
                    data-testid={`mobile-more-${id}`}
                    className={`w-full flex items-center gap-3 px-5 py-3.5 text-sm font-medium transition ${
                      locked
                        ? "text-[var(--brand-muted)] opacity-60"
                        : tab === id
                        ? "text-[var(--brand-primary)] bg-[var(--brand-bg)]"
                        : "text-[var(--brand-ink)] hover:bg-[var(--brand-bg)]"
                    }`}
                  >
                    <Icon className="w-5 h-5 shrink-0" /> {label} {locked && <span className="ml-auto"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>}
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
      <ModuleUpsellDialog moduleId={upsellModule} onClose={() => setUpsellModule(null)} onUpgrade={openModulesUpgrade} />
    </div>
    </CurrencyProvider>
  );
};





function AppRoute({ checking, adminUser, company, companyVersion, onAuthenticated, onLogout, loadCompany, sandboxInvite }) {
  const navigate = useNavigate();
  const [sub, setSub] = useState(undefined);
  const [verifying, setVerifying] = useState(false);
  const [demoStarting, setDemoStarting] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("demo") === "1";
  });
  const loadSub = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/billing/subscription`);
      setSub(r.data);
    } catch {
      setSub({ active: false, features: [] });
    }
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (false && params.get("demo") === "1" && !adminUser && !checking) {
      (async () => {
        try {
          const res = await axios.post(`${API}/auth/demo-login`);
          saveAdminToken(res.data.token);
          await onAuthenticated(res.data.user);
          window.history.replaceState({}, "", "/app");
        } catch {
          /* falls Demo nicht startet, normalen Login zeigen */
        } finally {
          setDemoStarting(false);
        }
      })();
    } else if (params.get("demo") === "1" && adminUser) {
      setDemoStarting(false);
      window.history.replaceState({}, "", "/app");
    }
  }, [adminUser, checking, onAuthenticated]);
  useEffect(() => {
    if (!adminUser) {
      setSub(undefined);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success" && params.get("session_id")) {
      return; // the checkout-success effect below activates & sets the subscription
    }
    loadSub();
  }, [adminUser, loadSub]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (adminUser && params.get("checkout") === "success" && params.get("session_id")) {
      setVerifying(true);
      axios.get(`${API}/billing/status/${params.get("session_id")}`)
        .then((r) => setSub(r.data))
        .catch(() => loadSub())
        .finally(() => {
          setVerifying(false);
          window.history.replaceState({}, "", "/app");
        });
    }
  }, [adminUser, loadSub]);
  const handleLogout = () => {
    onLogout();
    navigate("/app");
  };
  if (checking) return <FullScreenLoader />;
  if (!adminUser && demoStarting) return <FullScreenLoader />;
  if (!adminUser) return <LoginScreen onAuthenticated={onAuthenticated} sandboxInvite={sandboxInvite} />;
  if (sub === undefined || verifying) return <FullScreenLoader />;
  if (!sub.active) return <PlanSelection onLogout={handleLogout} onReload={loadSub} />;
  if (company === undefined) return <FullScreenLoader />;
  if (!company || !company.onboarding_completed) {
    return (
      <OnboardingWizard
        initial={company}
        onLogout={handleLogout}
        onDone={async () => {
          await loadCompany();
        }}
      />
    );
  }
  return (
    <Editor
      key={companyVersion}
      adminUser={adminUser}
      company={company}
      subscription={sub}
      onCompanyChange={loadCompany}
      onLogout={handleLogout}
    />
  );
}

function App() {
  const [adminUser, setAdminUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [company, setCompany] = useState(undefined); // undefined=loading, null=none, obj=loaded
  const [companyVersion, setCompanyVersion] = useState(0);
  const [sandboxInvite, setSandboxInvite] = useState(null);

  const loadCompany = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/company`);
      applyCompany(r.data);
      setCompany(r.data);
      setCompanyVersion((v) => v + 1);
      return r.data;
    } catch {
      setCompany(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const params = new URLSearchParams(window.location.search);
      const previewTok = params.get("preview");
      if (previewTok) {
        saveAdminToken(previewTok);
        axios.post(`${API}/demo/preview-viewed`, {}, { headers: { Authorization: `Bearer ${previewTok}` } }).catch(() => {});
        window.history.replaceState({}, "", "/app");
      }
      const personalDemoToken = params.get("demo_invite");
      if (personalDemoToken) {
        // Eine Sandbox-Einladung darf nie eine bereits geöffnete Suite-Sitzung
        // übernehmen. Der Link beendet deshalb zuerst die vorhandene Sitzung
        // und führt ausschließlich zur Registrierung bzw. Anmeldung der
        // eingeladenen Person.
        clearAdminToken();
        if (!cancelled) {
          setAdminUser(null);
          setCompany(undefined);
          setSandboxInvite(null);
        }
        try {
          const response = await axios.post(`${API}/auth/personal-demo-invite`, { token: personalDemoToken });
          if (response.data?.registration_required) {
            setSandboxInvite({
              token: personalDemoToken,
              email: response.data.email || "",
              name: response.data.name || "",
              company: response.data.company || "",
              hours: response.data.hours,
            });
          } else {
            saveAdminToken(response.data.token);
          }
        } catch (error) {
          clearAdminToken();
          if (!cancelled) toast.error(error.response?.data?.detail || "Die persönliche Demo-Einladung konnte nicht geöffnet werden");
        }
        window.history.replaceState({}, "", "/app");
      }
      const tok = getAdminToken();
      if (!tok) {
        if (!cancelled) setChecking(false);
        return;
      }
      try {
        const r = await axios.get(`${API}/auth/me`);
        if (!cancelled) {
          setAdminUser(r.data);
          await loadCompany();
        }
      } catch {
        clearAdminToken();
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadCompany]);

  const handleAuthenticated = useCallback(
    async (user) => {
      setAdminUser(user);
      setCompany(undefined);
      await loadCompany();
    },
    [loadCompany]
  );

  const handleLogout = useCallback(() => {
    clearAdminToken();
    setAdminUser(null);
    setCompany(undefined);
  }, []);

  // Google-SSO-Rückkehr: session_id im URL-Fragment VOR allem anderen verarbeiten
  if (window.location.hash && window.location.hash.includes("session_id=")) {
    return <GoogleAuthCallback />;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/app" replace />} />
          <Route path="/google-info" element={<PublicOAuthLanding />} />
          <Route path="/datenschutz" element={<LegalPage docKey="datenschutz" />} />
          <Route path="/google-api-datenschutz" element={<GoogleApiPrivacyPage />} />
          <Route path="/konto-loeschen" element={<AccountDeletionPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/agb" element={<LegalPage docKey="agb" />} />
          <Route path="/nutzungsbedingungen" element={<LegalPage docKey="agb" />} />
          <Route path="/impressum" element={<LegalPage docKey="impressum" />} />
          <Route path="/willkommen" element={<WelcomePage />} />
          <Route path="/willkommen-whitelabel" element={<WhiteLabelWelcomePage />} />
          <Route path="/danke" element={<WelcomePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/invite/:token" element={<InvitePage />} />
          <Route
            path="/app"
            element={
              <AppRoute
                checking={checking}
                adminUser={adminUser}
                company={company}
                companyVersion={companyVersion}
                onAuthenticated={handleAuthenticated}
                onLogout={handleLogout}
                loadCompany={loadCompany}
                sandboxInvite={sandboxInvite}
              />
            }
          />
          <Route path="/kampagne/:slug" element={<PublicCampaignLanding />} />
          <Route
            path="/p/:token"
            element={
              <CustomerGate>
                <PublicQuote />
              </CustomerGate>
            }
          />
          <Route
            path="/a/:nr"
            element={
              <CustomerGate>
                <PublicQuote />
              </CustomerGate>
            }
          />
        </Routes>
      </BrowserRouter>
      <UniversalAssistant />
    </div>
  );
}

export default App;
